import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0ffafa8e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "button-slider-container" }

import { computed, nextTick, ref, watch } from "vue";
import {
  Edge,
  MarkerType,
  Node,
  NodeProps,
  useVueFlow,
  VueFlow,
} from "@vue-flow/core";
import { ElSlider, ElTooltip } from "element-plus";
import { Background } from "@vue-flow/background";
import { cloneDeep, uniqueId } from "lodash-es";
import { v4 as uuidv4 } from "uuid";
import { showConfirm } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import { checkIsLoop, checkIsLoopEnd, checkIsLoopStep } from "@/core/util";
import {
  collectAllRouterStepOrigIds,
  findRouterByRouteId,
  getHiddenExitEdgeSignature,
  getRouterLoopContext,
  getRouterExitSyncSignature,
  isNestedInsideRouterInSameLoop,
  isNestedRouterInSameLoop,
  syncAllRouterExitConnections,
} from "@/flow/utils/routerUtils";
import {
  collectAllOrigIds,
  createRemovalGraphLookup,
  findStepByOrigId,
  isNodeDetachedFromFlow,
} from "@/flow/utils/nodeUtils";
import {
  createLoopNeighborLookup,
  createNextLoopEndLookup,
  createRouterBranchLookup,
} from "@/flow/utils/nodeRenderLookups";
import { createEdgeRouterLookup } from "@/flow/utils/edgeRouterLookup";
import {
  findPreviousLoopStep,
  getLoopContextForStep,
  getLoopEndOrigId,
  getLoopStartOrigId,
  getLoopStepCount,
  getLoopSteps,
  getParentLoopOrigId,
  getDefaultLoopConfig,
} from "@/flow/utils/loopUtils";
import {
  collectVisibleLoopRemovalOrigIds,
  isVisibleLoopRemovalEdge,
} from "@/flow/utils/loopRemovalUtils";
import { syncLoopUiData } from "@/flow/utils/syncLoopUiData";
import {
  applyHorizontalAddShift,
  applyHorizontalRemoveShift,
  applyHorizontalShrinkShift,
  collectStructureSteps,
  compactRouterSiblingBranchesLeft,
  getListBounds,
  getStepsBounds,
  getStructureRange,
  ROUTE_VERTICAL_GAP,
  shiftRouteSiblingBranchesUp,
  shiftStructureSteps,
  STRUCTURE_HORIZONTAL_GAP,
} from "@/flow/utils/incrementalAddShift";
import {
  createLoopEndUiData,
  createLoopStartUiData,
  createLoopStepUiData,
  createRouterInsideLoopUiData,
  parseUiData,
  updateLoopStepsInUiData,
} from "@/flow/utils/uiDataUtils";
import {
  getUnplacedDetachedStepOrigIds,
  getVisibleConnectedStepOrigIds,
} from "@/flow/utils/detachedLayoutUtils";
import {
  DETACHED_MARKER_COLOR,
  DEFAULT_MARKER_COLOR,
  getEdgeMarkerColor,
} from "@/flow/utils/loopEdgeUtils";
import { assignStepNumber } from "@/flow/utils/stepNumberUtils";
import {
  collectActiveLoopReferenceOrigIds,
  enableSkippedStep,
  isStepSkipped,
  normalizeFlowSkipState,
  skipStep,
} from "@/flow/utils/skipUtils";
import {
  getVisibleOutgoingStepTargetOrigId,
  isVisibleIncomingStepEdge,
  resolveStepRemovalReconnectTarget,
} from "@/flow/utils/stepRemovalReconnectUtils";
import CustomEdge from "./CustomEdge.vue";
import CustomNode from "./CustomNode.vue";

export interface CustomData {
  type?: string;
  [key: string]: any;
}
export interface CustomEvents {
  [onCustomEvent: string]: (event: MouseEvent) => void;
}
export type CustomNodeTypes = "custom" | "router" | "start" | "end";
// eslint-disable-next-line
type CustomNode = Node<CustomData, CustomEvents, CustomNodeTypes> & {
  predecessor?: string;
  successor?: string | string[];
};
const baseViewportX = 280;
const baseViewportY = 50;
type CustomNodeProps = NodeProps<CustomData, CustomEvents, CustomNodeTypes>;

export default /*#__PURE__*/_defineComponent({
  __name: 'FlowDiagram',
  props: {
    steps: {},
    flow: {},
    selectedStep: {},
    selectedIndex: {},
    listIndex: {},
    clonedStep: {},
    backgroundColor: {},
    logs: {},
    nodesDraggable: { type: Boolean },
    flowLines: {},
    newEdge: {},
    startStepPosition: {},
    showOrganizeSteps: { type: Boolean },
    drawerVisible: { type: Boolean },
    flowLog: {},
    enableHorizontalTrackpadPan: { type: Boolean },
    showInputSummary: { type: Boolean }
  },
  emits: [
  "open-drawer",
  "close-drawer",
  "close-router",
  "update",
  "open-router",
  "show-condition",
  "open-delay",
  "clone-step",
  "open-output",
  "open-details",
  "update-step-positions",
  "output-list-changed",
  "open-loop",
  "update-changed-list",
  "filter-removed",
  "invalid-loop-change",
],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const zoomScale = ref(100);
const oldZoomScale = ref(100);

const defaultViewport = { zoom: 1.3, x: baseViewportX, y: baseViewportY };

const { setViewport, viewport, fitView } = useVueFlow();

const zoom = (newZoom) => {
  const zoomValue = (newZoom / 200) * 2.6;
  setViewport({ zoom: zoomValue, x: baseViewportX, y: baseViewportY });
};

const handleWheelCapture = (event: WheelEvent) => {
  if (!props.enableHorizontalTrackpadPan) {
    return;
  }

  const target = event.target as HTMLElement | null;
  const isInsideCanvas = !!target?.closest(".validationflow");
  const horizontalIntent =
    Math.abs(event.deltaX) > 0 &&
    Math.abs(event.deltaX) > Math.abs(event.deltaY);

  if (!isInsideCanvas || !horizontalIntent || event.ctrlKey) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  setViewport({
    x: viewport.value.x - (event.deltaX / viewport.value.zoom) * 0.5,
    y: viewport.value.y,
    zoom: viewport.value.zoom,
  });
};

const locateFlow = () => {
  const ids = nodes.value.map((n) => n.id).filter(Boolean);

  if (!ids.length) {
    setViewport({ zoom: 1.3, x: baseViewportX, y: baseViewportY });
    return;
  }

  fitView({
    nodes: ids,
    padding: 0.3,
    duration: 400,
    maxZoom: 1.3,
  });
};

const handleOrganizeClick = () => {
  emit("update-step-positions", cloneDeep(stepList.value), true);

  nextTick(() => {
    setTimeout(() => {
      locateFlow();
    }, 0);
  });
};

__expose({
  locateFlow,
  updateRouteNameLabel,
  resetInitialLoad() {
    isInitialLoad.value = true;
  },
});

watch(
  viewport,
  (newViewport) => {
    zoomScale.value = Math.round((newViewport.zoom / 2.6) * 200);
  },
  { deep: true }
);

const emit = __emit;
const { onEdgeClick, onEdgeDoubleClick, onPaneClick } = useVueFlow();
const { t } = i18n.global;
const nodes = ref<CustomNode[]>([]);
const stepList = ref<any[]>([]);
const edgeRouterLookup = computed(() => createEdgeRouterLookup(stepList.value));
const loopNeighborLookup = computed(() =>
  createLoopNeighborLookup(stepList.value)
);
const routerBranchLookup = computed(() =>
  createRouterBranchLookup(nodes.value)
);
const activeLoopReferenceOrigIds = computed(() =>
  collectActiveLoopReferenceOrigIds(stepList.value)
);
const SKIPPED_MARKER_COLOR = DETACHED_MARKER_COLOR;
const getNodeByOrigId = (origId?: string | null) => {
  if (!origId || origId === "start") return null;
  return (
    nodes.value.find((node) => String(node?.data?.origId) === String(origId)) ??
    null
  );
};
const canStartConnectToOrigId = (origId?: string | null) => {
  if (!origId || origId === "start") return false;

  const targetRes = findStepByOrigId(stepList.value, String(origId));
  if (!targetRes?.step) return false;

  const isRootTarget = targetRes.steps === stepList.value;
  const isRouterTarget = targetRes.step?.type === "ROUTER";
  const isLoopTarget = checkIsLoop(targetRes.step);
  const isLoopEndTarget = checkIsLoopEnd(targetRes.step);

  return isRootTarget && !isRouterTarget && !isLoopTarget && !isLoopEndTarget;
};
const hasOnlyDisconnectedSourceLinks = (step?: any) => {
  const sourceLinks = Array.isArray(step?.source?.stepLinkData)
    ? step.source.stepLinkData
    : [];

  return (
    sourceLinks.length > 0 &&
    sourceLinks.every((link: any) => link?.connected === false)
  );
};
const isDetachedEdge = (edge?: Edge) => {
  if (!edge || edge.hidden) return false;

  const sourceNode = getNodeByOrigId(String(edge.source ?? ""));
  const targetNode = getNodeByOrigId(String(edge.target ?? ""));

  return (
    isNodeDetachedFromFlow(sourceNode?.data) &&
    isNodeDetachedFromFlow(targetNode?.data)
  );
};
const getVisualEdgeMarkerColor = (edge?: Edge) => {
  if (!edge) return DEFAULT_MARKER_COLOR;
  if (edge.hidden) return "transparent";
  if (isDetachedEdge(edge)) return DETACHED_MARKER_COLOR;

  const sourceNode = getNodeByOrigId(String(edge.source ?? ""));
  const targetNode = getNodeByOrigId(String(edge.target ?? ""));
  if (isStepSkipped(sourceNode?.data) || isStepSkipped(targetNode?.data)) {
    return SKIPPED_MARKER_COLOR;
  }

  const markerColor = String((edge.markerEnd as any)?.color ?? "")
    .trim()
    .toLowerCase();
  if (markerColor === "red" || markerColor === "#ff0000") {
    return edge.markerEnd?.color ?? "red";
  }

  return getEdgeMarkerColor(edge);
};
const restoreEdgeMarkerColor = (edge?: Edge) => {
  if (!edge) return;
  const defaultWidth =
    // eslint-disable-next-line
    typeof edge.markerEnd?.width === "number"
      ? edge.markerEnd.width
      : edge.hidden
      ? 0
      : 7;
  edge.markerEnd = {
    type: MarkerType.ArrowClosed,
    width: defaultWidth,
    color: getVisualEdgeMarkerColor(edge),
  };
};

const selectedNodeId = ref<null | string>(null);
const edges = ref<Edge[]>([]);
const nextLoopEndLookup = computed(() =>
  createNextLoopEndLookup(nodes.value, edges.value)
);
const removalGraph = computed(() =>
  createRemovalGraphLookup(nodes.value, edges.value)
);
const lastInvalidLoopEmitted = ref<boolean | null>(null);
const visibleEdges = computed(() => {
  return edges.value.filter((e) => !e.hidden);
});

const edgeGraphSignature = computed(() =>
  edges.value
    .map((e: any) =>
      [
        e?.id,
        e?.source,
        e?.target,
        e?.sourceHandle,
        e?.targetHandle,
        e?.hidden ? 1 : 0,
      ]
        .map((x) => String(x ?? ""))
        .join("|")
    )
    .join(";")
);

const loopEndpointSignature = computed(() => {
  const loopBackEdges = edges.value.filter((e) => isLoopBackEdge(e));

  return loopBackEdges
    .map((edge: any) => {
      const endId = String(edge?.source ?? "");
      const startId = String(edge?.target ?? "");

      const startNode = nodes.value.find(
        (n: any) => String(n?.data?.origId) === startId
      );
      const endNode = nodes.value.find(
        (n: any) => String(n?.data?.origId) === endId
      );

      const startOk = checkIsLoop(startNode?.data) ? 1 : 0;
      const endOk = checkIsLoopEnd(endNode?.data) ? 1 : 0;
      return `${String(edge?.id ?? "")}|${startOk}|${endOk}`;
    })
    .join(";");
});

const loopValiditySignature = computed(
  () => `${edgeGraphSignature.value}||${loopEndpointSignature.value}`
);
const routerExitSyncSignature = computed(() =>
  getRouterExitSyncSignature(stepList.value, edges.value)
);
const ROUTER_ROUTE_VERTICAL_GAP = ROUTE_VERTICAL_GAP;
const pendingLayoutChangedOrigIds = new Set<string>();

function getShiftScopeOrigIds(anchorOrigId?: string | null) {
  const movableOrigIds = getVisibleConnectedStepOrigIds(
    stepList.value,
    edges.value,
    anchorOrigId ?? "start"
  );

  getUnplacedDetachedStepOrigIds(
    stepList.value,
    edges.value,
    ROUTE_VERTICAL_GAP
  ).forEach((origId) => movableOrigIds.add(origId));

  return movableOrigIds;
}

function applyScopedHorizontalAddShift({
  insertedRange,
  targetList,
  anchorOrigId,
}: {
  insertedRange: { startIndex: number; endIndex: number };
  targetList: any[];
  anchorOrigId?: string | null;
}) {
  const movableOrigIds = getShiftScopeOrigIds(anchorOrigId);
  applyHorizontalAddShift({
    insertedRange,
    rootSteps: stepList.value,
    targetList,
    movableOrigIds,
  });
  movableOrigIds.forEach((origId) => pendingLayoutChangedOrigIds.add(origId));
  syncNodePositionsFromSteps(stepList.value);
}

function syncNodePositionsFromSteps(list: any[]) {
  const nodeByOrigId = new Map<string, any>();
  nodes.value.forEach((node: any) => {
    const origId = String(node?.data?.origId ?? "");
    if (origId) {
      nodeByOrigId.set(origId, node);
    }
  });

  const visit = (stepsList: any[]) => {
    if (!Array.isArray(stepsList)) return;

    stepsList.forEach((step: any) => {
      const node = nodeByOrigId.get(String(step?.origId ?? ""));

      if (node && step?.position) {
        node.position = { ...step.position };
        node.data.position = { ...step.position };
      }

      if (Array.isArray(step?.routes)) {
        step.routes.forEach((route: any) => visit(route?.steps || []));
      }
    });
  };

  visit(list);
}

function consumePendingLayoutChangedExtra(extra: any) {
  if (!pendingLayoutChangedOrigIds.size) return extra;

  const existing = Array.isArray(extra?.layoutChangedOrigIds)
    ? extra.layoutChangedOrigIds
    : Array.from(extra?.layoutChangedOrigIds ?? []);
  const layoutChangedOrigIds = Array.from(
    new Set([...existing, ...Array.from(pendingLayoutChangedOrigIds)])
  );
  pendingLayoutChangedOrigIds.clear();

  return {
    ...(extra ?? {}),
    layoutChangedOrigIds,
  };
}

function resolveShiftScopeAnchor(
  targetList: any[],
  index: number,
  preferredOrigId?: string | null
) {
  if (preferredOrigId && findStepByOrigId(stepList.value, preferredOrigId)) {
    return preferredOrigId;
  }

  return (
    targetList?.[index]?.origId ??
    targetList?.[Math.max(0, index - 1)]?.origId ??
    preferredOrigId ??
    "start"
  );
}

function getCursorAfterStructureRemoval(
  targetList: any[],
  removedIndex: number,
  removedBounds: any
): number | null {
  if (!Array.isArray(targetList) || !removedBounds) return null;

  let cursorX = removedBounds.minX;

  if (removedIndex > 0) {
    const previousRange = getStructureRange(targetList, removedIndex - 1);
    const previousBounds = getStepsBounds(
      collectStructureSteps(targetList, previousRange)
    );

    if (previousBounds) {
      cursorX = Math.max(
        cursorX,
        previousBounds.maxX + STRUCTURE_HORIZONTAL_GAP
      );
    }
  }

  return cursorX;
}

function compactAfterStructureRemoval(
  targetList: any[],
  removedIndex: number,
  removedBounds: any,
  anchorOrigId?: string | null
): void {
  const cursorX = getCursorAfterStructureRemoval(
    targetList,
    removedIndex,
    removedBounds
  );

  if (cursorX === null) return;

  const scopeAnchorOrigId = resolveShiftScopeAnchor(
    targetList,
    removedIndex,
    anchorOrigId
  );

  applyHorizontalRemoveShift({
    rootSteps: stepList.value,
    targetList,
    startIndex: removedIndex,
    cursorX,
    gap: STRUCTURE_HORIZONTAL_GAP,
    routeGap: ROUTER_ROUTE_VERTICAL_GAP,
    movableOrigIds: getShiftScopeOrigIds(scopeAnchorOrigId),
  });
}

function compactAfterRouteRemoval(
  routerStep: any,
  removedRouteIndex: number,
  removedBounds: any,
  anchorOrigId?: string | null
): void {
  if (!routerStep || !removedBounds) return;

  let cursorY = removedBounds.minY;

  if (removedRouteIndex > 0) {
    const previousRouteBounds = getListBounds(
      routerStep.routes?.[removedRouteIndex - 1]?.steps || []
    );

    if (previousRouteBounds) {
      cursorY = Math.max(
        cursorY,
        previousRouteBounds.maxY + ROUTER_ROUTE_VERTICAL_GAP
      );
    }
  }

  shiftRouteSiblingBranchesUp(
    routerStep,
    removedRouteIndex,
    cursorY,
    ROUTER_ROUTE_VERTICAL_GAP,
    getShiftScopeOrigIds(anchorOrigId ?? routerStep.origId)
  );
  compactRouterSiblingBranchesLeft(
    routerStep,
    STRUCTURE_HORIZONTAL_GAP,
    getShiftScopeOrigIds(anchorOrigId ?? routerStep.origId)
  );

  const routerResult = findStepByOrigId(stepList.value, routerStep.origId);
  if (!routerResult) return;

  applyHorizontalShrinkShift({
    rootSteps: stepList.value,
    targetList: routerResult.steps,
    structureIndex: routerResult.index,
    gap: STRUCTURE_HORIZONTAL_GAP,
    routeGap: ROUTER_ROUTE_VERTICAL_GAP,
    movableOrigIds: getShiftScopeOrigIds(anchorOrigId ?? routerStep.origId),
  });
}

const dark = ref(false);
const selectedEdge = ref<null | Edge>(null);
const selectedEdgeNodeId = ref<null | string>(null);
const clearSelectedEdge = () => {
  const edgeToRestore = selectedEdge.value;

  selectedEdge.value = null;
  selectedEdgeNodeId.value = null;

  if (edgeToRestore) {
    restoreEdgeMarkerColor(edgeToRestore);
  }
};
const loopStartStep = ref<string>("");
const isWebhookStep = ref(false);
const isInitialLoad = ref(true);
const pendingChainAttachKeys = new Set<string>();

const connectionExists = (param: string, path: string) =>
  edges.value.some((edge) => edge?.[path] === param && !edge.hidden);

const asNodeProps = (p: unknown) => p as CustomNodeProps;

function getStepSortKey(step: any) {
  const candidates = [
    typeof step?.index === "number" ? step.index : null,
    typeof step?.ix === "number" ? step.ix : null,
    typeof step?.indexValue === "number" ? step.indexValue : null,
    typeof step?.stepPosition?.x === "number" ? step.stepPosition.x : null,
  ].filter((v) => typeof v === "number" && !Number.isNaN(v));

  return candidates.length ? candidates[0] : 999999;
}

function normalizeStepOrder(stepsArr: any[]) {
  if (!Array.isArray(stepsArr) || stepsArr.length <= 1) {
    if (Array.isArray(stepsArr)) {
      stepsArr.forEach((s) => {
        if (Array.isArray(s?.routes)) {
          s.routes.forEach((r: any) => normalizeStepOrder(r?.steps || []));
        }
      });
    }
    return stepsArr;
  }

  const stable = stepsArr.map((s, i) => ({ s, i }));
  stable.sort((a, b) => {
    const ak = getStepSortKey(a.s);
    const bk = getStepSortKey(b.s);
    if (ak !== bk) return ak - bk;
    return a.i - b.i;
  });

  stepsArr.splice(0, stepsArr.length, ...stable.map((x) => x.s));

  stepsArr.forEach((s) => {
    if (Array.isArray(s?.routes)) {
      s.routes.forEach((r: any) => normalizeStepOrder(r?.steps || []));
    }
  });

  return stepsArr;
}
function breakLoopParentCycles(visitStepList: any[]): void {
  // Defensive: after complex drag/drop edits parentLoopOrigId can become cyclic.
  // Break cycles to avoid infinite traversals and corrupted nesting.
  const order = new Map<string, number>();
  const stepsByOrigId = new Map<string, any>();
  let counter = 0;

  const visit = (steps: any[]) => {
    if (!Array.isArray(steps)) return;
    steps.forEach((step) => {
      if (!step) return;
      const origId = step?.origId ? String(step.origId) : null;
      if (origId && !order.has(origId)) {
        order.set(origId, counter);
        counter += 1;
      }
      if (origId) {
        stepsByOrigId.set(origId, step);
      }
      if (Array.isArray(step.routes)) {
        step.routes.forEach((route: any) => {
          visit(route?.steps || []);
        });
      }
    });
  };

  visit(visitStepList);

  const loopByOrigId = new Map<string, any>();
  stepsByOrigId.forEach((step, origId) => {
    if (step?.type === "LOOP") {
      loopByOrigId.set(origId, step);
    }
  });

  const orderOf = (origId: string): number =>
    order.get(origId) ?? Number.MAX_SAFE_INTEGER;

  loopByOrigId.forEach((_, startId) => {
    const visited = new Set<string>();
    const path: string[] = [];

    let current: string | null = startId;
    while (current) {
      if (visited.has(current)) {
        const startIndex = path.indexOf(current);
        const cycle =
          startIndex >= 0 ? path.slice(startIndex) : [String(current)];

        let cutAny = false;
        cycle.forEach((loopId) => {
          const loop = loopByOrigId.get(loopId);
          const parentId = loop?.parentLoopOrigId
            ? String(loop.parentLoopOrigId)
            : null;

          if (!parentId || !loopByOrigId.has(parentId)) return;

          // Parent loops should appear before their children in traversal order.
          if (orderOf(parentId) > orderOf(loopId)) {
            loop.parentLoopOrigId = null;
            cutAny = true;
          }
        });

        if (!cutAny && cycle.length) {
          const fallbackId = cycle[cycle.length - 1];
          const fallbackLoop = loopByOrigId.get(fallbackId);
          if (fallbackLoop) {
            fallbackLoop.parentLoopOrigId = null;
          }
        }

        break;
      }

      visited.add(current);
      path.push(current);

      const currentLoop = loopByOrigId.get(current);
      const parentId = currentLoop?.parentLoopOrigId
        ? String(currentLoop.parentLoopOrigId)
        : null;

      if (!parentId || !loopByOrigId.has(parentId)) break;
      current = parentId;
    }
  });
}
function syncAndEmitUpdate(
  {
    extra = null,
    onlyLineUpdate = false,
    initialLoad = false,
    edgesArr = edges.value,
  }: {
    list?: any;
    extra?: any;
    edgesArr?: any;
    onlyLineUpdate?: boolean;
    initialLoad?: boolean;
  } = {
    list: stepList.value,
    extra: null,
    onlyLineUpdate: false,
    edgesArr: edges.value,
    initialLoad: false,
  }
) {
  sortArrayBySourceTarget(edgesArr, "start");
  updateNodeList(edgesArr);
  reconcileVisibleEdgeAttachments(edgesArr);

  stepList.value = updateStepList(stepList.value);
  if (!initialLoad) {
    normalizeStepOrder(stepList.value);
  }
  // Keep loop uiData.steps + loopStep flags consistent (prevents output leakage outside loops).
  syncLoopUiData(stepList.value, edgesArr);
  // Extra guard: never allow cyclic parentLoopOrigId chains.
  breakLoopParentCycles(stepList.value);
  // Re-sync uiData so any cleared parentLoopOrigId doesn't get restored from stale uiData later.
  syncLoopUiData(stepList.value, edgesArr);
  syncSkipStateForCurrentFlow(stepList.value);

  update({
    list: stepList.value,
    extra,
    onlyLineUpdate,
    edgesArr,
    initialLoad,
  });
}

function syncSkipStateForCurrentFlow(list = stepList.value) {
  normalizeFlowSkipState(list);
  syncNodeSkipDataFromSteps(list);
}

function syncNodeSkipDataFromSteps(list = stepList.value) {
  nodes.value.forEach((node: any) => {
    const origId = String(node?.data?.origId ?? "");
    if (!origId || origId === "start") return;

    const found = findStepByOrigId(list, origId);
    if (!found?.step) return;

    node.data.uiData = found.step.uiData;
  });
}

function ensureRouterExitConnectionsSynced({
  emitUpdate = false,
  initialLoad = false,
}: {
  emitUpdate?: boolean;
  initialLoad?: boolean;
} = {}) {
  const beforeSignature = getHiddenExitEdgeSignature(edges.value);

  syncAllRouterExitConnections(stepList.value, edges.value);

  const afterSignature = getHiddenExitEdgeSignature(edges.value);
  if (beforeSignature === afterSignature) return;

  edges.value = [...edges.value];

  if (emitUpdate) {
    update({
      list: stepList.value,
      edgesArr: edges.value,
      onlyLineUpdate: true,
      initialLoad,
    });
  }
}

const openDrawer = (
  step,
  index,
  listIndex,
  options?: { skipSave?: boolean }
) => {
  if (props.nodesDraggable) {
    emit("open-drawer", step, index, listIndex, options);
  } else {
    emit("open-details", step);
  }
};

function selectStepAfterAdd(
  origId: string,
  kind: "node" | "router" | "loop" = "node"
) {
  if (!origId) return;
  nextTick(() => {
    const result = findStepByOrigId(stepList.value, origId);
    if (!result?.step) return;
    const listIndex = props.listIndex ?? "";
    const drawerListIndex = `step${listIndex}-${result.index}`;

    if (kind === "router") {
      openRouter(result.step, result.index, listIndex, {
        skipSave: true,
      });
    } else if (kind === "loop") {
      openLoop(result.step, result.index, listIndex, {
        skipSave: true,
      });
    } else {
      openDrawer(result.step, result.index, drawerListIndex, {
        skipSave: true,
      });
    }
  });
}
watch(
  () => props.drawerVisible,
  () => {
    if (props.drawerVisible) {
      clearSelectedEdge();

      oldZoomScale.value = zoomScale.value;
    } else {
      zoomScale.value = oldZoomScale.value;
      setViewport({
        zoom: (zoomScale.value / 200) * 2.6,
        x: baseViewportX,
        y: baseViewportY,
      });
    }
  },
  { deep: true, immediate: true }
);

function getDiagramSignature(list: any[]): string {
  if (!Array.isArray(list)) return "";

  function normalizeStep(item: any): any {
    return {
      id: item?.id,
      origId: item?.origId,
      name: item?.name,
      type: item?.type,
      active: item?.active,
      ix: item?.ix,
      iy: item?.iy,
      stepNumber: item?.stepNumber,
      parentId: item?.parentId,
      insideLoop: item?.insideLoop,
      parentLoopOrigId: item?.parentLoopOrigId,
      loopEndOrigId: item?.loopEndOrigId,
      lock: item?.lock,
      connectorSpecId: item?.connectorSpecId,
      connectionConnectorSpecId: item?.connection?.connector?.connectorSpecId,
      connectionActive: item?.connection?.active,
      positionX: item?.position?.x,
      positionY: item?.position?.y,
      uiData: item?.uiData,
      hasDelay: Boolean(item?.delay),
      hasOutput: Boolean(item?.output),
      filterLength: item?.filter?.length ?? 0,
      routes: Array.isArray(item?.routes)
        ? item.routes.map((route: any) => ({
            id: route?.id,
            name: route?.name,
            filterLength: route?.filter?.length ?? 0,
            steps: Array.isArray(route?.steps)
              ? route.steps.map(normalizeStep)
              : [],
          }))
        : [],
      steps: Array.isArray(item?.steps) ? item.steps.map(normalizeStep) : [],
    };
  }

  return JSON.stringify(list.map(normalizeStep));
}

let lastDiagramSignature = "";

watch(
  () => props.steps,
  () => {
    stepList.value = cloneDeep(props.steps);
    const nextDiagramSignature = getDiagramSignature(stepList.value);
    const diagramSignatureChanged =
      nextDiagramSignature !== lastDiagramSignature;
    lastDiagramSignature = nextDiagramSignature;
    if (stepList.value.length && diagramSignatureChanged) {
      updateNodes(stepList.value);
    }
    if (stepList.value.length === 1 && stepList.value[0]?.ix !== 0) {
      setTimeout(() => {
        update();
      }, 0);
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => props.newEdge,
  (newEdge) => {
    if (!newEdge) return;
    const { source, target } = newEdge;
    setTimeout(() => {
      addSimpleEdge(source, target, "bottom", "top", true);
      clearSkipMissingIncomingChain(target);
      updateNodeList(edges.value);
      stepList.value = updateStepList(stepList.value);
      update();
    }, 0);
  }
);

watch(
  () => edges.value.length,
  () => {
    if (edges.value.length === 0) return;
    const capturedInitialLoad = isInitialLoad.value;
    setTimeout(() => {
      syncAndEmitUpdate({
        initialLoad: capturedInitialLoad,
        list: stepList.value,
        edgesArr: edges.value,
        onlyLineUpdate: false,
      });
      isInitialLoad.value = false;
    }, 0);
  },
  { immediate: true }
);

watch(
  routerExitSyncSignature,
  () => {
    const capturedInitialLoad = isInitialLoad.value;
    nextTick(() => {
      ensureRouterExitConnectionsSynced({
        emitUpdate: true,
        initialLoad: capturedInitialLoad,
      });
    });
  },
  { immediate: true, flush: "post" }
);

watch(
  loopValiditySignature,
  () => {
    // Do not bail out when edges become empty: we still need to clear
    // invalidLoopBackConnection flags and emit invalid-loop-change(false).
    if (!nodes.value.length) {
      if (lastInvalidLoopEmitted.value !== false) {
        lastInvalidLoopEmitted.value = false;
        emit("invalid-loop-change", false);
      }
      return;
    }
    nextTick(() => {
      markInvalidLoopBackConnections(edges.value);
      if (edges.value.length) {
        // Force Vue Flow to recompute marker definitions / marker urls when only markerEnd changes.
        edges.value = [...edges.value];
      }
    });
  },
  { immediate: true }
);

function updateNodeList(edgesList) {
  const nodeByOrigId = new Map();
  nodes.value.forEach((node) => {
    nodeByOrigId.set(node.data.origId, node);
  });

  // Reset predecessor/successor
  nodes.value.forEach((node) => {
    if (node.id !== "start") {
      node.predecessor = undefined;
      node.successor = undefined;
    }
  });

  edgesList.forEach((edge) => {
    // Skip hidden edges for predecessor/successor
    if (edge.hidden) return;

    if (edge.source === "start") {
      const findTargetNode = nodeByOrigId.get(edge.target);
      if (findTargetNode) {
        findTargetNode.predecessor = "start";
      }
    } else {
      const findSourceNode = nodeByOrigId.get(edge.source);
      if (findSourceNode) {
        if (findSourceNode.type === "router") {
          if (!findSourceNode.successor?.includes(edge.target)) {
            if (Array.isArray(findSourceNode.successor)) {
              findSourceNode.successor.push(edge.target);
            } else {
              findSourceNode.successor = [];
              findSourceNode.successor.push(edge.target);
            }
          }
        } else if (checkIsLoop(findSourceNode?.data)) {
          const loopLength = getLoopStepCount(findSourceNode?.data);
          const shouldAddSuccessor =
            loopLength === 1 ||
            (loopLength > 1 && !edge.targetHandle?.includes("top"));

          if (
            shouldAddSuccessor &&
            !findSourceNode.successor?.includes(edge.target)
          ) {
            findSourceNode.successor = Array.isArray(findSourceNode.successor)
              ? findSourceNode.successor
              : [];
            findSourceNode.successor.push(edge.target);
          }
        } else {
          findSourceNode.successor = edge.target;
        }
      }
      const findTargetNode = nodeByOrigId.get(edge.target);
      if (findTargetNode) {
        findTargetNode.predecessor = edge.source;
      }
    }
  });
  const startNode = nodes.value.find((node) => node.predecessor === "start");
  if (startNode) {
    startNode.data.ix = 0;
    startNode.data.index = 0;
    startNode.data.stepPosition = {
      x: 0,
      y: startNode.data.stepPosition
        ? startNode.data.stepPosition.y
        : startNode.data.iy,
    };
    if (startNode.successor) {
      setIndex(startNode.successor, 1, nodes.value);
      // Send start node as mock to trigger syncInputData function inside useSyncInputData function.
      emit("update-changed-list", {
        extra: {
          changedStep: {
            ix: startNode.data.ix,
            iy: startNode.data.iy,
            id: startNode.data.id,
            uniqueId: startNode.data.uniqueId,
          },
        },
        edgesArr: edges.value,
        nodeList: nodes.value,
      });
    }
  }
  markInvalidLoopBackConnections(edgesList);
  markMissingIncomingChains(edgesList);
  const detachedEdgeStylesChanged = syncDetachedEdgeStyles(edgesList);
  if (detachedEdgeStylesChanged && edgesList === edges.value) {
    // Vue Flow can keep using stale marker defs when only markerEnd color changes.
    edges.value = [...edges.value];
  }
}

function syncDetachedEdgeStyles(edgesList: Edge[]) {
  let changed = false;

  edgesList.forEach((edge: any) => {
    if (!edge) return;

    const defaultWidth =
      // eslint-disable-next-line
      typeof edge.markerEnd?.width === "number"
        ? edge.markerEnd.width
        : edge.hidden
        ? 0
        : 7;
    const nextColor = getVisualEdgeMarkerColor(edge);
    const currentColor = String((edge.markerEnd as any)?.color ?? "");
    const currentWidth =
      typeof edge.markerEnd?.width === "number" ? edge.markerEnd.width : null;

    if (currentColor !== String(nextColor) || currentWidth !== defaultWidth) {
      changed = true;
    }

    edge.markerEnd = {
      type: MarkerType.ArrowClosed,
      width: defaultWidth,
      color: nextColor,
    };
  });

  return changed;
}

function markInvalidLoopBackConnections(edgesList: Edge[]) {
  const invalidLoopBackNodeIds = new Set<string>();

  const adjacency = new Map<string, Set<string>>();
  edgesList.forEach((edge) => {
    if (!edge || edge.hidden) return;
    if (!edge.source || !edge.target) return;
    if (isLoopBackEdge(edge)) return;
    const source = String(edge.source);
    const target = String(edge.target);
    if (!source || !target) return;
    if (!adjacency.has(source)) adjacency.set(source, new Set());
    const next = adjacency.get(source);
    if (next) {
      next.add(target);
    }
  });

  const loopPairs: Array<{ startId: string; endId: string }> = [];
  edgesList.forEach((edge) => {
    if (!edge || edge.hidden) return;
    if (!isLoopBackEdge(edge)) return;
    const endId = String(edge.source ?? "");
    const startId = String(edge.target ?? "");
    if (!startId || !endId) return;
    loopPairs.push({ startId, endId });
  });

  const reachableCache = new Map<string, Set<string>>();
  const computeReachable = (from: string): Set<string> => {
    const key = String(from);
    const cached = reachableCache.get(key);
    if (cached) return cached;

    const visited = new Set<string>();
    const stack: string[] = [key];
    while (stack.length) {
      const current = stack.pop();
      if (current && !visited.has(current)) {
        visited.add(current);
        const next = adjacency.get(current);
        if (next) {
          next.forEach((n) => {
            if (!visited.has(n)) stack.push(n);
          });
        }
      }
    }

    reachableCache.set(key, visited);
    return visited;
  };

  const canReach = (from: string, to: string): boolean => {
    if (!from || !to) return false;
    if (from === to) return true;
    return computeReachable(from).has(String(to));
  };

  // Rule 1: LOOP_END(top) -> LOOP(top) is only legal if LOOP is upstream of LOOP_END
  // in the *connection graph* (ignoring loop-back edges).
  loopPairs.forEach(({ startId, endId }) => {
    const startNode = nodes.value.find(
      (n: any) => String(n?.data?.origId) === startId
    );
    const endNode = nodes.value.find(
      (n: any) => String(n?.data?.origId) === endId
    );

    // Wrong types (or missing nodes) = invalid loop-back connection.
    if (!checkIsLoop(startNode?.data) || !checkIsLoopEnd(endNode?.data)) {
      invalidLoopBackNodeIds.add(startId);
      invalidLoopBackNodeIds.add(endId);
      return;
    }

    const startToEnd = canReach(startId, endId);
    const endToStart = canReach(endId, startId);
    if (!startToEnd || endToStart) {
      invalidLoopBackNodeIds.add(startId);
      invalidLoopBackNodeIds.add(endId);
    }
  });

  // Rule 2 (parentheses rule): nested loops cannot "cross".
  // Crossing pattern (in connection order): A.start -> B.start -> A.end -> B.end
  for (let i = 0; i < loopPairs.length; i += 1) {
    const a = loopPairs[i];
    for (let j = i + 1; j < loopPairs.length; j += 1) {
      const b = loopPairs[j];

      const aBeforeB =
        canReach(a.startId, b.startId) &&
        canReach(b.startId, a.endId) &&
        canReach(a.endId, b.endId);
      const bBeforeA =
        canReach(b.startId, a.startId) &&
        canReach(a.startId, b.endId) &&
        canReach(b.endId, a.endId);

      if (aBeforeB || bBeforeA) {
        invalidLoopBackNodeIds.add(a.startId);
        invalidLoopBackNodeIds.add(a.endId);
        invalidLoopBackNodeIds.add(b.startId);
        invalidLoopBackNodeIds.add(b.endId);
      }
    }
  }

  nodes.value.forEach((node: any) => {
    if (!node?.data?.origId || node.id === "start") return;
    node.data.invalidLoopBackConnection = invalidLoopBackNodeIds.has(
      String(node.data.origId)
    );
  });

  edgesList.forEach((edge: any) => {
    if (!edge || !isLoopBackEdge(edge)) return;
    const sourceId = String(edge.source ?? "");
    const targetId = String(edge.target ?? "");
    const isInvalid =
      invalidLoopBackNodeIds.has(sourceId) ||
      invalidLoopBackNodeIds.has(targetId);

    const defaultWidth =
      // eslint-disable-next-line
      typeof edge.markerEnd?.width === "number"
        ? edge.markerEnd.width
        : edge.hidden
        ? 0
        : 7;

    edge.markerEnd = {
      type: MarkerType.ArrowClosed,
      width: defaultWidth,
      color: isInvalid ? "red" : getEdgeMarkerColor(edge),
    };
  });

  // Defensive: marker color can get "stuck" as red if an edge is no longer a loop-back edge.
  edgesList.forEach((edge: any) => {
    if (!edge || isLoopBackEdge(edge)) return;
    if (!edge.markerEnd || typeof edge.markerEnd !== "object") return;

    const color = String(edge.markerEnd?.color || "")
      .trim()
      .toLowerCase();
    if (color !== "red" && color !== "#ff0000") return;

    const defaultWidth =
      // eslint-disable-next-line
      typeof edge.markerEnd?.width === "number"
        ? edge.markerEnd.width
        : edge.hidden
        ? 0
        : 7;

    edge.markerEnd = {
      type: MarkerType.ArrowClosed,
      width: defaultWidth,
      color: getEdgeMarkerColor(edge),
    };
  });

  const hasInvalidLoopBackConnection = invalidLoopBackNodeIds.size > 0;
  if (lastInvalidLoopEmitted.value !== hasInvalidLoopBackConnection) {
    lastInvalidLoopEmitted.value = hasInvalidLoopBackConnection;
    emit("invalid-loop-change", hasInvalidLoopBackConnection);
  }
}

function clearSkipMissingIncomingChain(targetOrigId: string) {
  if (!targetOrigId) return;
  const targetId = String(targetOrigId);
  const node = nodes.value.find((n) => n?.data?.origId === targetId);
  if (node?.data?.skipMissingIncomingChain) {
    node.data.skipMissingIncomingChain = false;
  }
  const step = findStepByOrigId(stepList.value, targetId)?.step;
  if (step?.skipMissingIncomingChain) {
    step.skipMissingIncomingChain = false;
  }
}

function markMissingIncomingChains(edgesList) {
  const adjacency = new Map<string, Set<string>>();
  const hasIncomingLeftTop = new Set<string>();

  edgesList.forEach((edge) => {
    if (edge.hidden) return;
    if (!edge.source || !edge.target) return;
    const source = String(edge.source);
    const target = String(edge.target);
    if (!adjacency.has(source)) {
      adjacency.set(source, new Set());
    }
    // eslint-disable-next-line
    adjacency.get(source)?.add(target);

    // Loop-back edges (LOOP_END(top) -> LOOP(top)) are internal edges and should not
    // count as an "incoming connection" for missing-incoming-chain highlighting.
    if (isLoopBackEdge(edge)) return;

    const targetHandle = String(edge.targetHandle || "");
    if (
      !targetHandle ||
      targetHandle.includes("__handle-left") ||
      targetHandle.includes("__handle-top")
    ) {
      hasIncomingLeftTop.add(target);
    }
  });

  const skipMissingTargets = new Set<string>();
  nodes.value.forEach((node) => {
    if (!node?.data) return;
    if (node.data.skipMissingIncomingChain && node.data.origId) {
      skipMissingTargets.add(String(node.data.origId));
    }
  });
  skipMissingTargets.forEach((id) => hasIncomingLeftTop.add(id));

  const missingChainIds = new Set<string>();
  const stack: string[] = [];

  nodes.value.forEach((node) => {
    if (!node?.data) return;
    const { origId } = node.data;
    if (!origId || origId === "start" || node.id === "start") return;
    if (!hasIncomingLeftTop.has(String(origId))) {
      stack.push(String(origId));
    }
  });

  while (stack.length > 0) {
    const current = stack.pop();
    // eslint-disable-next-line
    if (!current || missingChainIds.has(current)) continue;
    missingChainIds.add(current);
    const nextNodes = adjacency.get(current);
    // eslint-disable-next-line
    if (!nextNodes) continue;
    nextNodes.forEach((next) => {
      if (!missingChainIds.has(next)) {
        stack.push(next);
      }
    });
  }

  nodes.value.forEach((node) => {
    if (!node?.data) return;
    const { origId } = node.data;
    if (!origId || origId === "start" || node.id === "start") {
      node.data.missingIncomingChain = false;
      return;
    }
    node.data.missingIncomingChain = missingChainIds.has(String(origId));
  });
}

function setIndex(
  nodeId: string,
  index: number,
  nodesList: CustomNode[],
  visited = new Set<string>()
) {
  if (visited.has(nodeId)) return;
  visited.add(nodeId);

  const findNode = nodesList.find((node) => node.data.origId === nodeId);
  if (!findNode) return;

  findNode.data.ix = index;
  findNode.data.index = index;
  findNode.data.stepPosition = {
    x: index,
    y: findNode.data.stepPosition
      ? findNode.data.stepPosition.y
      : findNode.data.iy,
  };

  const successors = findNode.successor;
  if (!successors) return;

  const recurse = (nextId: string, nextIndex: number) =>
    setIndex(nextId, nextIndex, nodesList, visited);

  if (Array.isArray(successors)) {
    successors.forEach((n) => {
      if (!n) return;

      if (findNode.type === "router") {
        const successorNode = nodesList.find((node) => node.data.origId === n);

        if (successorNode && checkIsLoopEnd(successorNode.data)) {
          // Router connecting to LOOP_END - calculate index for it
          const loopEndIndex = calculateLoopEndIndexForRouter(
            findNode,
            nodesList
          );
          recurse(n, loopEndIndex);
        } else {
          // Route step - same index as router
          recurse(n, index);
        }
      } else {
        const nextIndex = setLoopSuccessorsIndex(findNode, n, nodesList);
        recurse(n, nextIndex);
      }
    });
  } else if (findNode.type === "router") {
    const successorNode = nodesList.find(
      (node) => node.data.origId === successors
    );

    if (successorNode && checkIsLoopEnd(successorNode.data)) {
      // Router connecting to LOOP_END - calculate index for it
      const loopEndIndex = calculateLoopEndIndexForRouter(findNode, nodesList);
      recurse(successors, loopEndIndex);
    } else {
      recurse(successors, index);
    }
  } else {
    recurse(successors, index + 1);
  }
}

function calculateLoopEndIndexForRouter(
  routerNode: CustomNode,
  nodesList: CustomNode[]
): number {
  const routerData = routerNode.data;
  let parentLoopOrigId: string | null = null;

  if (routerData?.uiData) {
    try {
      const uiData = parseUiData(routerData.uiData);
      if (uiData?.insideLoop && uiData?.parentLoopOrigId) {
        parentLoopOrigId = uiData.parentLoopOrigId;
      }
    } catch (e) {
      console.error("Failed to calculate loop end ix");
    }
  }

  if (!parentLoopOrigId) {
    return (routerData?.ix ?? 0) + 1;
  }

  const parentLoopNode = nodesList.find(
    (node) => node.data.origId === parentLoopOrigId
  );

  if (!parentLoopNode) {
    return (routerData?.ix ?? 0) + 1;
  }

  const loopEndOrigId = getLoopEndOrigId(parentLoopNode.data);
  // Prefer actual list span to avoid stale uiData (e.g., router not registered in loop steps)
  const loopStepCountFromList =
    loopEndOrigId && parentLoopOrigId
      ? (() => {
          const loopStartResult = findStepByOrigId(
            stepList.value,
            parentLoopOrigId
          );
          if (!loopStartResult) return null;
          const { steps, index: startIndex } = loopStartResult;
          const endIndex = steps.findIndex(
            (step) => step?.origId === loopEndOrigId
          );
          if (endIndex === -1 || endIndex <= startIndex) return null;
          return endIndex - startIndex - 1;
        })()
      : null;
  const loopStepCount =
    loopStepCountFromList ?? getLoopStepCount(parentLoopNode.data);
  const parentLoopIx =
    typeof parentLoopNode.data?.ix === "number" ? parentLoopNode.data.ix : 0;

  const computedIndex = parentLoopIx + loopStepCount + 1;
  const routerIx = typeof routerData?.ix === "number" ? routerData.ix : 0;

  return Math.max(computedIndex, routerIx + 1);
}

function setLoopSuccessorsIndex(findNode, n, nodeList) {
  if (checkIsLoop(findNode.data)) {
    const successor = nodeList.find((node) => node.data.origId === n);
    if (!successor) return findNode.data.ix + 1;

    // If successor is LOOP_END, it comes after all loop steps
    if (checkIsLoopEnd(successor.data)) {
      return getLoopStepCount(findNode.data) + findNode.data.ix + 1;
    }

    // Check if successor is a Router inside this loop
    if (successor.data.type === "ROUTER") {
      const uiData = parseUiData(successor.data.uiData);
      if (
        uiData?.insideLoop &&
        uiData?.parentLoopOrigId === findNode.data.origId
      ) {
        return findNode.data.ix + 1;
      }
    }

    if (!checkIsLoopStep(successor.data)) {
      return getLoopStepCount(findNode.data) + findNode.data.ix + 1;
    }
    if (checkIsLoop(findNode.data) && checkIsLoopStep(findNode.data)) {
      return successor.data.ix;
    }
    return findNode.data.ix + 1;
  }
  return findNode.data.ix + 1;
}

onPaneClick(() => {
  clearSelectedEdge();
});

onEdgeClick((event) => {
  if (isLoopBackEdge(event.edge)) {
    clearSelectedEdge();
    return;
  }
  if (selectedEdge.value?.id !== event.edge.id) {
    clearSelectedEdge();
  }

  selectedEdge.value = event.edge;
  const { target } = selectedEdge.value;
  const targetNode = nodes.value.find((node) => node.data.origId === target);
  isWebhookStep.value =
    targetNode?.data?.connection?.connector?.connectorSpecId?.includes(
      "webhook"
    );
  selectedEdgeNodeId.value = event.edge.data.id;
});

onEdgeDoubleClick((event) => {
  if (isLoopBackEdge(event.edge)) {
    return;
  }
  if (event.edge?.data?.type !== "ROUTER") {
    const index = Number(event.edge.label) - 1;
    openFilter(index, event.edge.sourceNode.data);
  }
});

window.addEventListener("keydown", (event) => {
  if (
    selectedEdge.value &&
    (event.key === "Delete" || event.key === "Backspace") &&
    props.nodesDraggable
  ) {
    const isStartEdge = selectedEdge.value.source === "start";
    if (
      (!isStartEdge && isWebhookStep.value) ||
      isLoopBackEdge(selectedEdge.value)
    ) {
      return;
    }
    handleRemoveEdge();
  }
});

function handleConnect(params: {
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
}) {
  const { source, target, sourceHandle, targetHandle } = params;

  if (!source || !target) return;
  if (source === target) return;

  if (source === "start" || target === "start") {
    if (target === "start") return;
    if (source !== "start") return;
    if (!sourceHandle?.includes("__handle-right")) return;
    if (!targetHandle?.includes("__handle-left")) return;

    if (!canStartConnectToOrigId(target)) {
      console.warn("start can only connect to root non-router non-loop steps", {
        target,
      });
      return;
    }

    const targetRes = findStepByOrigId(stepList.value, target);
    if (!targetRes?.step) return;

    const targetHasIncoming = connectionExists(target, "target");
    if (targetHasIncoming) return;

    edges.value = edges.value.filter(
      (e) => e.source !== "start" && e.target !== "start"
    );

    if (targetRes.index > 0) {
      const moved = targetRes.steps.splice(targetRes.index, 1)[0];
      stepList.value.unshift(moved);
    }

    edges.value.push({
      id: `e-start-${target}`,
      source: "start",
      target,
      sourceHandle,
      targetHandle,
      label: `Edge start-${target}`,
      type: "custom",
      animated: false,
      data: {},
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 7,
        color: DEFAULT_MARKER_COLOR,
      },
    });

    normalizeStepOrder(stepList.value);
    updateNodeList(edges.value);
    stepList.value = updateStepList(stepList.value);

    update({
      list: stepList.value,
      edgesArr: edges.value,
      onlyLineUpdate: false,
      initialLoad: false,
    });

    setTimeout(() => emit("output-list-changed"), 0);
    return;
  }

  // The step connected from "start" must not have any other incoming connections.
  const startTarget = edges.value.find(
    (e) => e.source === "start" && !e.hidden
  )?.target;
  if (startTarget && target === startTarget) return;

  // Find the step by ID
  const sourceStepResult = findStepByOrigId(stepList.value, source);
  const targetStepResult = findStepByOrigId(stepList.value, target);

  if (!sourceStepResult?.step || !targetStepResult?.step) {
    console.warn("handleConnect skipped: step not found", {
      source,
      target,
      sourceStepResult,
      targetStepResult,
    });
    return;
  }

  // Loop start/end are linked internally (including the loop-back edge) and must not
  // be connected manually to each other in the editor.
  const isSourceLoopStart = checkIsLoop(sourceStepResult.step);
  const isSourceLoopEnd = checkIsLoopEnd(sourceStepResult.step);
  const isTargetLoopStart = checkIsLoop(targetStepResult.step);
  const isTargetLoopEndStep = checkIsLoopEnd(targetStepResult.step);
  if (
    (isSourceLoopStart && isTargetLoopEndStep) ||
    (isSourceLoopEnd && isTargetLoopStart)
  ) {
    return;
  }

  const isRouterSource = sourceStepResult.step.type === "ROUTER";
  const isTargetRouter = targetStepResult.step.type === "ROUTER";
  const isTopSourceHandle = sourceHandle?.includes("__handle-top");
  const isBottomSourceHandle = sourceHandle?.includes("__handle-bottom");
  const isRightSourceHandle = sourceHandle?.includes("__handle-right");
  const isTopTargetHandle = targetHandle?.includes("__handle-top");
  const isLeftTargetHandle = targetHandle?.includes("__handle-left");
  const targetHasVisibleIncoming = edges.value.some(
    (edge) => edge.target === target && !edge.hidden && !isLoopBackEdge(edge)
  );
  const canAttachDetachedChainToRouter =
    isRouterSource &&
    isBottomSourceHandle &&
    isTopTargetHandle &&
    !targetHasVisibleIncoming;

  const sourceRouteId = sourceStepResult.step?.parentId;
  const targetRouteId = targetStepResult.step?.parentId;
  const sourceRouterFromRoute = sourceRouteId
    ? findRouterByRouteId(stepList.value, sourceRouteId)
    : null;
  const targetRouterFromRoute = targetRouteId
    ? findRouterByRouteId(stepList.value, targetRouteId)
    : null;
  const sourceIsRouteFirstStep =
    Boolean(sourceRouteId) &&
    sourceStepResult.index === 0 &&
    sourceStepResult.steps !== stepList.value &&
    Boolean(sourceRouterFromRoute);
  const targetIsRouteFirstStep =
    Boolean(targetRouteId) &&
    targetStepResult.index === 0 &&
    targetStepResult.steps !== stepList.value &&
    Boolean(targetRouterFromRoute);

  // Router route "first steps" cannot be connected to each other (between routes of the same router).
  if (
    sourceIsRouteFirstStep &&
    targetIsRouteFirstStep &&
    sourceRouteId &&
    targetRouteId &&
    sourceRouteId !== targetRouteId &&
    sourceRouterFromRoute?.origId &&
    sourceRouterFromRoute.origId === targetRouterFromRoute?.origId
  ) {
    return;
  }

  // Router route "first steps" can only be connected into from the top
  // (specifically: router(bottom) -> firstStep(top) from the owning router).
  if (targetIsRouteFirstStep) {
    const owningRouterOrigId = targetRouterFromRoute?.origId;
    const isFromOwningRouter =
      isRouterSource && sourceStepResult.step.origId === owningRouterOrigId;
    if (!(isFromOwningRouter && isBottomSourceHandle && isTopTargetHandle)) {
      return;
    }
  }

  // Router nodes can only be connected into from their left handle.
  if (isTargetRouter && !isLeftTargetHandle) return;

  // Starting a connection from the top handle is only allowed for loop-back
  // (LOOP_END(top) -> LOOP(top)).
  if (isTopSourceHandle) {
    if (!isTopTargetHandle) return;
    if (!checkIsLoopEnd(sourceStepResult.step)) return;
    if (!checkIsLoop(targetStepResult.step)) return;
    const expectedLoopStart = getLoopStartOrigId(sourceStepResult.step);
    if (!expectedLoopStart || expectedLoopStart !== target) return;
  }

  // Connecting into a top handle is only allowed for router-bottom edges or loop-back.
  // Detached chains are also attached through the top handle when they become a new router route.
  if (isTopTargetHandle && !isTopSourceHandle) {
    if (isTargetLoopStart) return;
    if (!targetIsRouteFirstStep && !canAttachDetachedChainToRouter) return;
    if (!(isRouterSource && isBottomSourceHandle)) return;
  }

  if (!isRouterSource && !isTopSourceHandle) {
    // Non-router connections can only start from the right and end on the left.
    if (!isRightSourceHandle) return;
    if (!isLeftTargetHandle) return;
  }

  // Special case: router right-handle -> LOOP_END acts as an explicit "exit" edge.
  const isRouterExitHandle =
    isRouterSource && sourceHandle?.includes("__handle-right");
  const routerLoopContext = isRouterSource
    ? getRouterLoopContext(sourceStepResult.step)
    : { insideLoop: false, loopEndOrigId: null, parentLoopOrigId: null };
  const isTargetLoopEnd = checkIsLoopEnd(targetStepResult.step);
  const localParentLoop =
    isRouterSource && Array.isArray(sourceStepResult?.steps)
      ? findPreviousLoopStep(sourceStepResult.steps, sourceStepResult.index)
      : null;
  const localLoopEndOrigId = localParentLoop
    ? getLoopEndOrigId(localParentLoop)
    : null;
  const isRouterExitToOwnLoopEnd =
    isRouterExitHandle &&
    isTargetLoopEnd &&
    routerLoopContext.insideLoop &&
    routerLoopContext.loopEndOrigId === target;
  const isRouterExitToLocalLoopEnd =
    isRouterExitHandle &&
    isTargetLoopEnd &&
    localLoopEndOrigId &&
    localLoopEndOrigId === target;
  const isRouterExitToValidLoopEnd =
    isRouterExitToOwnLoopEnd || isRouterExitToLocalLoopEnd;
  const exitLoopOrigId = isRouterExitToLocalLoopEnd
    ? localParentLoop?.origId
    : routerLoopContext.parentLoopOrigId;

  if (
    isRouterExitToValidLoopEnd &&
    isNestedRouterInSameLoop(
      sourceStepResult.step,
      stepList.value,
      exitLoopOrigId ?? undefined
    )
  ) {
    return;
  }

  if (isRouterSource) {
    if (isRouterExitHandle) {
      if (!isRouterExitToValidLoopEnd) return;
      if (!isLeftTargetHandle) return;
    } else if (!isBottomSourceHandle || !isTopTargetHandle) {
      return;
    }
  }

  if (targetStepResult.step.origId === sourceStepResult.step.origId) return;
  const bothStepRouter =
    sourceStepResult?.step?.type === "ROUTER" &&
    targetStepResult?.step?.type === "ROUTER";
  const animated = sourceStepResult?.step?.type === "ROUTER";

  const sourceIsLoopEnd = checkIsLoopEnd(sourceStepResult.step);
  const isLoopEndRightConnect =
    sourceIsLoopEnd && sourceHandle?.includes("__handle-right");

  if (isLoopEndRightConnect) {
    edges.value = edges.value.filter(
      (e) =>
        !(e.source === source && e.sourceHandle?.includes("__handle-right"))
    );
  }

  const sourceHandleExists = edges.value.some(
    (edge) =>
      edge.source === source &&
      edge.sourceHandle === sourceHandle &&
      !edge.hidden &&
      !isLoopBackEdge(edge)
  );

  const targetHandleExists = edges.value.some(
    (edge) =>
      edge.target === target &&
      edge.targetHandle === targetHandle &&
      !edge.hidden &&
      !isLoopBackEdge(edge)
  );
  const edgeAlreadyExists = edges.value.some(
    (edge) =>
      edge.source === source &&
      edge.target === target &&
      edge.sourceHandle === sourceHandle &&
      edge.targetHandle === targetHandle &&
      !edge.hidden
  );

  // Early return if both steps are routers
  if (bothStepRouter) return;
  if (edgeAlreadyExists) {
    if (reconcileVisibleEdgeAttachments(edges.value)) {
      syncAndEmitUpdate({
        initialLoad: false,
        list: stepList.value,
        edgesArr: edges.value,
        onlyLineUpdate: false,
      });
    }
    return;
  }

  const targetIsVirtualStep =
    isTargetRouter ||
    checkIsLoop(targetStepResult.step) ||
    checkIsLoopEnd(targetStepResult.step);

  // Virtual steps cannot have multiple incoming edges into the same handle.
  if (targetIsVirtualStep && targetHandleExists) {
    return;
  }

  // A handle can have only one outgoing edge; incoming edges are allowed to fan-in.
  // Router bottom is the exception because it can fan out to multiple routes.
  // Router exit edges are handled below by replacing the existing right-handle edge.
  if (
    sourceHandleExists &&
    !(isRouterSource && isBottomSourceHandle) &&
    !isLoopEndRightConnect &&
    !isRouterExitToValidLoopEnd
  ) {
    return;
  }

  if (isRouterExitToValidLoopEnd) {
    // This is an exit edge; ensure router loop context matches the loop end it exits to.
    if (isRouterExitToLocalLoopEnd && localParentLoop?.origId) {
      const nextUiData = createRouterInsideLoopUiData(
        localParentLoop.origId,
        target
      );
      if (sourceStepResult.step.uiData !== nextUiData) {
        sourceStepResult.step.uiData = nextUiData;
      }
      sourceStepResult.step.insideLoop = true;
      sourceStepResult.step.parentLoopOrigId = localParentLoop.origId;
      sourceStepResult.step.loopEndOrigId = target;
    }

    edges.value = edges.value.filter((e) => {
      if (e?.hidden) return true;
      if (e?.source !== source) return true;
      if (typeof e?.sourceHandle !== "string") return true;
      return !e.sourceHandle.includes("__handle-right");
    });

    const exists = edges.value.some(
      (e) =>
        e.source === source &&
        e.target === target &&
        !e.hidden &&
        e.sourceHandle === sourceHandle &&
        e.targetHandle === targetHandle
    );

    if (!exists) {
      edges.value.push({
        id: `edge-${source}-${target}`,
        source,
        target,
        sourceHandle,
        targetHandle,
        label: `Edge ${source}-${target}`,
        type: "custom",
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 7,
          color: DEFAULT_MARKER_COLOR,
        },
      });
      clearSkipMissingIncomingChain(target);
    }

    setTimeout(() => {
      syncAndEmitUpdate({
        initialLoad: false,
        list: stepList.value,
        edgesArr: edges.value,
        onlyLineUpdate: false,
      });
      emit("output-list-changed");
    }, 0);
    return;
  }

  const { chain: chainCandidates } = buildConnectedChainSteps(
    targetStepResult.steps,
    targetStepResult.index
  );
  const chainIds = chainCandidates.map((step) => step?.origId).filter(Boolean);
  const sourceInChain = chainIds.includes(source);
  const chainContainsSourceDeep = chainContainsOrigIdDeep(
    chainCandidates,
    source
  );
  const canAttachDetachedChainIgnoringCycle =
    chainIds.length > 0 &&
    !sourceInChain &&
    !hasExternalIncomingEdges(chainIds);
  const shouldDeferCycleAttach =
    canAttachDetachedChainIgnoringCycle && chainContainsSourceDeep;
  const shouldAttachDetachedChain =
    chainIds.length > 0 &&
    !sourceInChain &&
    !chainContainsSourceDeep &&
    !hasExternalIncomingEdges(chainIds);
  const detachedChain = shouldAttachDetachedChain
    ? detachChainFromList(targetStepResult.steps, chainCandidates)
    : [];

  // Add the edge to the edges array
  edges.value.push({
    id: `edge-${source}-${target}`,
    source,
    target,
    sourceHandle,
    targetHandle,
    label: `Edge ${source}-${target}`,
    type: "custom",
    animated,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 7,
      color: DEFAULT_MARKER_COLOR,
    },
  });

  if (
    sourceStepResult?.step?.type === "ROUTER" &&
    !targetStepResult?.step?.isRouterConnection &&
    detachedChain.length
  ) {
    attachChainToNewRoute(sourceStepResult.step, detachedChain);
  } else if (detachedChain.length) {
    attachChainAfterSource({
      chain: detachedChain,
      destinationSteps: sourceStepResult.steps,
      sourceOrigId: sourceStepResult.step.origId,
      parentId: sourceStepResult.step?.parentId,
    });
  }

  if (shouldDeferCycleAttach) {
    pendingChainAttachKeys.add(`${source}>>${target}`);
  }
  flushPendingChainAttaches();

  if (isLoopEndRightConnect) {
    setTimeout(() => {
      syncAndEmitUpdate({
        initialLoad: false,
        list: stepList.value,
        edgesArr: edges.value,
        onlyLineUpdate: false,
      });

      emit("output-list-changed");
    }, 0);
    return;
  }

  if (checkIsLoopStep(sourceStepResult?.step)) {
    const refreshedTargetResult = findStepByOrigId(stepList.value, target);
    if (Array.isArray(refreshedTargetResult?.steps)) {
      const loop = findPreviousLoopStep(
        refreshedTargetResult?.steps,
        refreshedTargetResult?.index
      );
      if (loop && refreshedTargetResult?.step) {
        setAndUpdateLoopStep(
          stepList.value,
          refreshedTargetResult?.step,
          loop.origId
        );
        updateLoopStep(
          stepList.value,
          refreshedTargetResult.step,
          sourceStepResult.index + 1,
          "add"
        );
      }
    }
  }
  setTimeout(() => {
    emit("output-list-changed");
  }, 0);
}

function updateStepList(steps) {
  const nodeByOrigId = new Map();
  nodes.value.forEach((node, idx) => {
    nodeByOrigId.set(node.data.origId, { node, idx });
  });

  function processSteps(stepsList) {
    stepsList.forEach((step) => {
      if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
        const entry = nodeByOrigId.get(step.origId);
        if (entry) {
          const { node: findNode, idx: findNodeIndex } = entry;
          step.ix = findNode.data.ix;
          step.index = findNode.data.index;
          step.stepPosition = {
            x: findNode.data.index,
            y: step.stepPosition ? step.stepPosition.y : step.iy,
          };
          const isDetached =
            findNode.successor === undefined &&
            findNode.predecessor === undefined;
          step.active = step.skipMissingIncomingChain ? true : !isDetached;
          nodes.value[findNodeIndex].data.active = step.active;
        }
      } else {
        const entry = nodeByOrigId.get(step.origId);
        if (entry) {
          const { node: findNode, idx: findNodeIndex } = entry;
          step.ix = findNode.data.ix;
          step.index = findNode.data.index;
          step.stepPosition = {
            x: findNode.data.index,
            y: step.stepPosition ? step.stepPosition.y : step.iy,
          };
          const isDetached =
            findNode.successor === undefined &&
            findNode.predecessor === undefined;
          step.active = step.skipMissingIncomingChain ? true : !isDetached;
          nodes.value[findNodeIndex].data.active = step.active;
        }
        if (step.routes) {
          step.routes.forEach((items) => {
            processSteps(items.steps);
          });
        }
      }
    });
  }

  processSteps(steps);
  return steps;
}

function sortArrayBySourceTarget(arr: any[], startNode: string) {
  // Find the element with source "start"
  const startElement = arr.find((item) => item.source === startNode);

  if (!startElement) {
    return;
  }

  const sortedArray = [startElement];
  let currentElement = startElement;
  let indexValue = 1;
  let prevIndexValue = 1; // to store the previous index value for animation

  while (
    sortedArray.length < arr.length &&
    currentElement &&
    currentElement.source
  ) {
    if (currentElement) {
      const currentTarget = currentElement.target;
      // eslint-disable-next-line
      const nextElement = arr.filter((item) => item.source === currentTarget);
      // eslint-disable-next-line
      nextElement.forEach((x, i) => {
        if (x?.source && i === 0) {
          const desireStep = findStepByOrigId(stepList.value, x?.source);

          if (x.animated) {
            desireStep.step.indexValue = prevIndexValue; // use the previous index value for animated elements
          } else {
            desireStep.step.indexValue = indexValue;
            prevIndexValue = indexValue; // update the previous index value
            indexValue++; // increment for non-animated elements
          }
        } else {
          let newIndex = 0;
          const desireStep = findStepByOrigId(stepList.value, x?.target);

          if (desireStep?.step) {
            if (x.animated) {
              desireStep.step.indexValue = prevIndexValue + 1; // use previous index value for animation
            } else {
              desireStep.step.indexValue = newIndex;
              prevIndexValue = newIndex; // update previous index value
              newIndex++;
            }
          }
        }
      });
      currentElement = [nextElement];
    }
  }
}
function disableContextMenu(event) {
  event.preventDefault();
}
function handleKeyDown(event: KeyboardEvent) {
  if (event.key === "Backspace") {
    event.preventDefault();
    event.stopPropagation();
  }
}

function setAndUpdateLoopStep(steps, currentStep, loopId) {
  steps.forEach((step) => {
    if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
      if (step.origId === currentStep.origId) {
        step.uiData = createLoopStepUiData(loopId);
      }
    } else if (step.routes) {
      step.routes.forEach((route) => {
        setAndUpdateLoopStep(route.steps, currentStep, loopId);
      });
    }
  });
}

function getHandleSuffix(
  handle: string | undefined,
  origId: string,
  fallback: string
) {
  if (!handle) return fallback;

  const prefix = `${origId}__handle-`;
  if (handle.startsWith(prefix)) {
    return handle.slice(prefix.length) || fallback;
  }

  return handle.includes("__handle-")
    ? handle.split("__handle-").pop() || fallback
    : handle;
}

function ensureDefaultMarker(edge: Edge) {
  const markerWidth =
    typeof edge.markerEnd?.width === "number" ? edge.markerEnd.width : 7;

  edge.markerEnd = {
    type: MarkerType.ArrowClosed,
    width: markerWidth,
    color: DEFAULT_MARKER_COLOR,
  };
}

function reconnectSteps({
  source,
  target,
  sourceHandle = "right",
  targetHandle = "left",
  animated = false,
  mode = "simple",
  edge,
}: {
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  animated?: boolean;
  mode?: "simple" | "step" | "reuse";
  edge?: Edge | null;
}) {
  if (!source || !target) return;

  prepareDetachedChainForReconnect(source, target, sourceHandle, targetHandle);

  if (mode === "reuse" && edge) {
    const existingEdgeIndex = edges.value.findIndex(
      (existingEdge) => existingEdge === edge
    );
    const sourceSuffix = getHandleSuffix(
      edge.sourceHandle,
      source,
      sourceHandle
    );
    const targetSuffix = getHandleSuffix(
      edge.targetHandle,
      target,
      targetHandle
    );

    edge.id = edge.id || `e${source}-${target}`;
    edge.source = source;
    edge.target = target;
    edge.sourceHandle = `${source}__handle-${sourceSuffix}`;
    edge.targetHandle = `${target}__handle-${targetSuffix}`;
    edge.hidden = false;
    edge.animated = animated;
    ensureDefaultMarker(edge);
    if (existingEdgeIndex === -1) {
      edges.value.push(edge);
    } else {
      edges.value = edges.value.map((existingEdge, index) =>
        index === existingEdgeIndex ? edge : existingEdge
      );
    }
    clearSkipMissingIncomingChain(target);
    return;
  }

  if (mode === "step") {
    addStepEdge(source, target, sourceHandle, targetHandle);
    return;
  }

  addSimpleEdge(source, target, sourceHandle, targetHandle, animated);
}

function addStepEdge(
  source: string,
  target: string,
  sourceHandle = "right",
  targetHandle = "left"
) {
  if (source === "start") {
    addSimpleEdge(source, target, sourceHandle, targetHandle, false);
    return;
  }

  const sourceHandlePrefix = `${source}__handle-`;
  const desiredSourceHandle = `${source}__handle-${sourceHandle}`;

  // When inserting via "+" we must not leave multiple outgoing edges from the same handle.
  // Prefer splitting the existing outgoing edge from this handle (if any),
  // rather than relying on list adjacency (which can diverge after reconnects).
  const outgoingFromHandle = edges.value
    .map((e, idx) => ({ e, idx }))
    .filter(({ e }) => {
      if (!e || e.hidden) return false;
      if (e.source !== source) return false;
      if (isLoopBackEdge(e)) return false;
      const sh = typeof e.sourceHandle === "string" ? e.sourceHandle : "";
      if (!sh) return false;
      return sh === desiredSourceHandle;
    });

  const splitEntry = outgoingFromHandle.length ? outgoingFromHandle[0] : null;
  const splitEdge = splitEntry?.e ?? null;

  // If a bug already created duplicates from the same handle, drop all but the first.
  if (outgoingFromHandle.length > 1) {
    const toDrop = new Set(outgoingFromHandle.slice(1).map((x) => x.idx));
    edges.value = edges.value.filter((_, idx) => !toDrop.has(idx));
  }

  let handleSuffix = sourceHandle;
  if (splitEdge && typeof splitEdge.sourceHandle === "string") {
    if (splitEdge.sourceHandle.startsWith(sourceHandlePrefix)) {
      handleSuffix = splitEdge.sourceHandle.slice(sourceHandlePrefix.length);
    }
  }

  const newEdgeSourceHandle = `${source}__handle-${handleSuffix}`;
  const newEdgeTargetHandle = `${target}__handle-${targetHandle}`;

  const newEdgeExists = edges.value.some(
    (e) =>
      !e?.hidden &&
      e.source === source &&
      e.target === target &&
      e.sourceHandle === newEdgeSourceHandle &&
      !isLoopBackEdge(e)
  );

  if (!newEdgeExists) {
    edges.value.push({
      id: `e${source}-${target}`,
      type: "custom",
      source,
      target,
      sourceHandle: newEdgeSourceHandle,
      targetHandle: newEdgeTargetHandle,
      animated: false,
      data: {},
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 7,
        color: DEFAULT_MARKER_COLOR,
      },
    });
  }

  // If there was an existing outgoing edge from the handle, split it so it becomes:
  // source -> target (new edge) and target -> oldTarget (rewired old edge).
  if (splitEdge && splitEdge.target && splitEdge.target !== target) {
    const oldTarget = String(splitEdge.target);
    splitEdge.id = `e${target}-${oldTarget}`;
    splitEdge.source = target;
    splitEdge.sourceHandle = `${target}__handle-${handleSuffix}`;

    const markerWidth =
      typeof splitEdge.markerEnd?.width === "number"
        ? splitEdge.markerEnd.width
        : 7;
    splitEdge.markerEnd = {
      type: MarkerType.ArrowClosed,
      width: markerWidth,
      color: DEFAULT_MARKER_COLOR,
    };
  } else {
    // Fallback to previous behavior (list adjacency) when there is no edge to split.
    const stepFound = findStepByOrigId(stepList.value, target);
    if (stepFound) {
      const { steps, index } = stepFound;
      const nextStep = steps[index + 1];
      if (nextStep?.origId) {
        const expectedSourceHandlePrefix = `${source}__handle-${handleSuffix}`;
        const stepEdge = edges.value.find((edge) => {
          if (edge?.hidden) return false;
          if (edge.source !== source) return false;
          if (edge.target !== nextStep.origId) return false;
          if (isLoopBackEdge(edge)) return false;

          const sh =
            typeof edge.sourceHandle === "string" ? edge.sourceHandle : "";
          return sh.startsWith(expectedSourceHandlePrefix);
        });
        if (stepEdge) {
          stepEdge.id = `e${target}-${nextStep.origId}`;
          stepEdge.source = target;
          stepEdge.sourceHandle = `${target}__handle-${handleSuffix}`;
          const markerWidth =
            typeof stepEdge.markerEnd?.width === "number"
              ? stepEdge.markerEnd.width
              : 7;
          stepEdge.markerEnd = {
            type: MarkerType.ArrowClosed,
            width: markerWidth,
            color: DEFAULT_MARKER_COLOR,
          };
        }
      }
    }
  }

  clearSkipMissingIncomingChain(target);
}

function hasVisibleEdgeBetween(source: string, target: string) {
  if (!source || !target) return false;

  return edges.value.some(
    (edge) => !edge?.hidden && edge.source === source && edge.target === target
  );
}

function addSimpleEdge(
  source: string,
  target: string,
  sourceHandle = "right",
  targetHandle = "left",
  animated = false
) {
  if (!source || !target) return;
  if (source === "start" && !canStartConnectToOrigId(target)) return;

  const edgeId = `e${source}-${target}-${sourceHandle}`;

  // Check if edge already exists (including checking by source/target/handle combination)
  const exists = edges.value.some(
    (e) =>
      e.source === source &&
      e.target === target &&
      e.sourceHandle?.includes(sourceHandle) &&
      !e.hidden
  );
  if (exists) return;

  edges.value.push({
    id: edgeId,
    type: "custom",
    source,
    target,
    sourceHandle: `${source}__handle-${sourceHandle}`,
    targetHandle: `${target}__handle-${targetHandle}`,
    animated,
    hidden: false,
    data: {},
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 7,
      color: DEFAULT_MARKER_COLOR,
    },
  });
  clearSkipMissingIncomingChain(target);
}

function normalizeRemovalEdgeValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return String(value);
}

function edgeMatchesSelectedEdge(
  edge: Edge | null | undefined,
  selected: Edge
) {
  if (!edge) return false;

  const edgeId = normalizeRemovalEdgeValue(edge.id);
  const selectedId = normalizeRemovalEdgeValue(selected.id);
  if (edgeId && selectedId && edgeId === selectedId) {
    return true;
  }

  return (
    normalizeRemovalEdgeValue(edge.source) ===
      normalizeRemovalEdgeValue(selected.source) &&
    normalizeRemovalEdgeValue(edge.target) ===
      normalizeRemovalEdgeValue(selected.target) &&
    normalizeRemovalEdgeValue(edge.sourceHandle) ===
      normalizeRemovalEdgeValue(selected.sourceHandle) &&
    normalizeRemovalEdgeValue(edge.targetHandle) ===
      normalizeRemovalEdgeValue(selected.targetHandle)
  );
}

function removeSelectedVisibleEdge(edgeToRemove: Edge): Edge | null {
  let removedEdge: Edge | null = null;

  if (edgeToRemove.source === "start") {
    const startEdge = edges.value.find((edge) => {
      if (!edge || edge.hidden) return false;
      return edgeMatchesSelectedEdge(edge, edgeToRemove);
    });

    if (!startEdge) return null;

    const dataObj =
      startEdge.data && typeof startEdge.data === "object"
        ? startEdge.data
        : {};
    startEdge.hidden = true;
    startEdge.data = {
      ...dataObj,
      hidden: true,
    };
    startEdge.markerEnd = {
      type: MarkerType.ArrowClosed,
      width: 0,
      color: "transparent",
    };

    return startEdge;
  }

  edges.value = edges.value.filter((edge) => {
    if (removedEdge) return true;
    if (edge?.hidden) return true;
    if (!edgeMatchesSelectedEdge(edge, edgeToRemove)) return true;

    removedEdge = edge;
    return false;
  });

  return removedEdge;
}

function countVisibleNodeEdges(
  nodeOrigId: string,
  direction: "incoming" | "outgoing",
  exclude?: (edge: Edge) => boolean
) {
  if (!nodeOrigId) return 0;

  return edges.value.reduce((count, edge) => {
    if (!edge || edge.hidden) return count;
    if (exclude?.(edge)) return count;

    const currentOrigId =
      direction === "incoming"
        ? normalizeRemovalEdgeValue(edge.target)
        : normalizeRemovalEdgeValue(edge.source);

    return currentOrigId === nodeOrigId ? count + 1 : count;
  }, 0);
}

function syncStateAfterVisibleEdgeRemoval(edgeToRemove: Edge): Edge | null {
  const removedEdge = removeSelectedVisibleEdge(edgeToRemove);
  if (!removedEdge) return null;

  updateNodeList(edges.value);

  const targetOrigId = normalizeRemovalEdgeValue(removedEdge.target);
  const targetNode = nodes.value.find(
    (node) => node.data.origId === targetOrigId
  );
  const targetHasVisibleIncoming =
    countVisibleNodeEdges(targetOrigId, "incoming", isLoopBackEdge) > 0;

  if (
    !targetHasVisibleIncoming &&
    targetNode &&
    isConditionChildNode(targetNode)
  ) {
    detachConditionChain(targetNode);
  }

  [
    normalizeRemovalEdgeValue(removedEdge.source),
    normalizeRemovalEdgeValue(removedEdge.target),
  ]
    .filter(Boolean)
    .forEach((origId) => {
      const affectedNode = nodes.value.find(
        (node) => node.data.origId === origId
      );
      if (!affectedNode) return;

      const isDetached =
        affectedNode.successor === undefined &&
        affectedNode.predecessor === undefined;

      if (isDetached && checkIsLoopStep(affectedNode.data)) {
        detachLoopStepFromLoop(affectedNode.data);
      }
    });

  stepList.value = updateStepList(stepList.value);
  return removedEdge;
}

async function handleRemoveEdge() {
  if (!selectedEdge.value) return;

  const edgeToRemove = selectedEdge.value;
  const { source, target, sourceHandle } = edgeToRemove;
  const sourceStepResult = findStepByOrigId(stepList.value, source);
  const targetStepResult = findStepByOrigId(stepList.value, target);
  const filterStep = sourceStepResult?.step;
  const filterStepId = filterStep?.id;
  const hasFilter =
    Array.isArray(filterStep?.filter) && filterStep.filter.length > 0;
  const routeId = targetStepResult?.step?.parentId;
  const routerStep = routeId
    ? findRouterByRouteId(stepList.value, routeId)
    : null;
  const shouldRemoveSingleConditionRouter =
    sourceStepResult?.step?.type === "ROUTER" &&
    routerStep?.origId === source &&
    Array.isArray(routerStep?.routes) &&
    routerStep.routes.length === 1;

  if (shouldRemoveSingleConditionRouter) {
    const routerTitle = t("Common.DeleteLine");
    const routerMessage = t("Common.DeleteConditionEdgeMessage");

    const isConfirmed = await showConfirm(
      "warning",
      routerTitle,
      routerMessage
    );

    if (!isConfirmed) {
      return;
    }

    clearSelectedEdge();

    setTimeout(() => {
      const targetNode = nodes.value.find(
        (node) => node.data.origId === target
      );

      if (targetNode && isConditionChildNode(targetNode)) {
        detachConditionChain(targetNode);
      }

      if (routerStep?.id) {
        // Edge deletion removes the router as a consequence.
        handleRemoveNode({
          id: routerStep.id,
          step: routerStep,
        });
      }

      if (props.selectedStep?.type === "ROUTER") {
        emit("close-router");
      }
    }, 0);

    return;
    // eslint-disable-next-line
  } else if (hasFilter) {
    const isConfirmed = await showConfirm(
      "warning",
      t("Common.DeleteLine"),
      t("Common.DeleteFilterEdgeMessage")
    );

    if (!isConfirmed) {
      return;
    }
  }

  // Special case: removing a router exit edge should not detach the whole condition chain.
  const isRouterExitEdgeToLoopEnd =
    sourceStepResult?.step?.type === "ROUTER" &&
    typeof sourceHandle === "string" &&
    sourceHandle.includes("__handle-right") &&
    !!targetStepResult?.step &&
    checkIsLoopEnd(targetStepResult.step);

  clearSelectedEdge();

  setTimeout(() => {
    if (isRouterExitEdgeToLoopEnd) {
      const removedEdge = syncStateAfterVisibleEdgeRemoval(edgeToRemove);
      if (!removedEdge) return;

      syncAndEmitUpdate({
        initialLoad: false,
        list: stepList.value,
        edgesArr: edges.value,
        onlyLineUpdate: false,
      });

      if (props.selectedStep?.type === "ROUTER") {
        emit("close-router");
      }

      return;
    }

    const removedEdge = syncStateAfterVisibleEdgeRemoval(edgeToRemove);
    if (!removedEdge) return;

    syncAndEmitUpdate({
      initialLoad: false,
      list: stepList.value,
      edgesArr: edges.value,
      onlyLineUpdate: false,
    });

    if (hasFilter && filterStepId) {
      removeFilter(filterStepId);
      emit("filter-removed", filterStepId);
    }

    if (props.selectedStep?.type === "ROUTER") {
      emit("close-router");
    }
  }, 0);
}

const findRouterStepRecursive = (steps: any[] = [], parentId: string) => {
  const direct = steps.find(
    (s) =>
      Array.isArray(s?.routes) && s.routes.some((r: any) => r?.id === parentId)
  );
  if (direct) return direct;

  return steps.reduce<any | null>((found, s) => {
    if (found) return found;
    if (!Array.isArray(s?.routes)) return null;

    return s.routes.reduce<any | null>((routeFound, r: any) => {
      if (routeFound) return routeFound;
      return findRouterStepRecursive(r?.steps || [], parentId);
    }, null);
  }, null);
};

const isConditionChildNode = (node: any) =>
  node?.data?.parentId &&
  node?.data?.conditionPosition &&
  node?.data?.conditionUniqueId;

function normalizeDetachedUniqueId(entity: any) {
  if (typeof entity?.uniqueId === "string") {
    entity.uniqueId =
      typeof entity?.index === "number"
        ? entity.index
        : entity?.stepPosition?.x ?? entity.uniqueId;
  }
}

function clearConditionMetadata(entity: any) {
  if (!entity) return;
  delete entity.parentId;
  delete entity.isRouterConnection;
  entity.conditionPosition = null;
  delete entity.conditionUniqueId;
  normalizeDetachedUniqueId(entity);
}

function detachLoopStepFromLoop(loopStepData: any) {
  const loopStartOrigId = getParentLoopOrigId(loopStepData);

  if (!loopStartOrigId) return;

  const loop = findStepByOrigId(stepList.value, loopStartOrigId);
  const loopSteps = getLoopSteps(loop?.step);
  const loopStepIndex = loopSteps.findIndex(
    (step) => step.origId === loopStepData.origId
  );

  if (loopStepIndex === -1) return;

  updateLoopStep(stepList.value, loopStepData, loopStepIndex, "remove");
  delete loopStepData.uiData;
  removeLoopStepUiData(stepList.value, loopStepData);
}

function detachConditionChain(node: any) {
  const { parentId } = node.data;
  clearConditionMetadata(node.data);

  const stepItem = findStepByOrigId(stepList.value, node.data.origId);

  if (stepItem && stepItem.step) {
    clearConditionMetadata(stepItem.step);
  }

  const routerStep = findRouterStepRecursive(stepList.value, parentId);

  if (!routerStep) return;

  const route = routerStep.routes.find((r: any) => r.id === parentId);
  if (!route || !Array.isArray(route.steps)) return;

  const idxInRoute = route.steps.findIndex(
    (s: any) => s.origId === node.data.origId
  );

  if (idxInRoute === -1) return;

  const detachedSteps = route.steps.splice(idxInRoute);

  // Drop the empty route to remove the orphaned condition after detaching.
  if (route.steps.length === 0) {
    const routeIndex = routerStep.routes.findIndex(
      (r: any) => r.id === parentId
    );
    if (routeIndex !== -1) {
      routerStep.routes.splice(routeIndex, 1);
    }
  }

  detachedSteps.forEach((detachedStep: any) => {
    clearConditionMetadata(detachedStep);

    const alreadyInRoot = stepList.value.some(
      (s: any) => s.origId === detachedStep.origId
    );

    if (!alreadyInRoot) {
      stepList.value.push(detachedStep);
      // flow.value.detachedSteps = flow.value.detachedSteps || [];
      // flow.value.detachedSteps.push(detachedStep);
    }

    const detachedNode = nodes.value.find(
      (n: any) => n.data.origId === detachedStep.origId
    );

    if (detachedNode) {
      clearConditionMetadata(detachedNode.data);
    }
  });
}

function isLoopBackEdge(edge: Edge) {
  return (
    edge?.sourceHandle?.includes("__handle-top") &&
    edge?.targetHandle?.includes("__handle-top")
  );
}

function buildConnectedChainSteps(
  steps: any[],
  startIndex: number,
  edgesList: Edge[] = edges.value
): { chain: any[]; steps: any[] } {
  if (!Array.isArray(steps)) return { chain: [], steps };

  const stepsRef = steps;
  const startIndexRef = startIndex;

  if (startIndexRef < 0 || startIndexRef >= stepsRef.length) {
    return { chain: [], steps: stepsRef };
  }

  const indexById = new Map<string, number>();
  stepsRef.forEach((step, idx) => {
    if (step?.origId) {
      indexById.set(step.origId, idx);
    }
  });

  const chain: any[] = [];
  const visited = new Set<number>();
  let currentIndex = startIndexRef;

  while (currentIndex >= 0 && currentIndex < stepsRef.length) {
    if (visited.has(currentIndex)) break;
    visited.add(currentIndex);

    const current = stepsRef[currentIndex];
    if (!current?.origId) break;
    chain.push(current);

    const nextCandidates = edgesList
      .filter(
        (edge) =>
          edge.source === current.origId &&
          !isLoopBackEdge(edge) &&
          !edge.hidden
      )
      .map((edge) => ({
        idx: indexById.get(edge.target),
        edge,
      }))
      .filter(({ idx }) => typeof idx === "number" && !visited.has(idx));

    const forward = nextCandidates
      // eslint-disable-next-line
      .filter(({ idx }) => (idx as number) > currentIndex)
      .sort((a, b) => (a.idx as number) - (b.idx as number))[0];

    const nextIndex = forward
      ? (forward.idx as number)
      : nextCandidates.sort((a, b) => (a.idx as number) - (b.idx as number))[0]
          ?.idx;

    if (typeof nextIndex !== "number") break;
    currentIndex = nextIndex;
  }

  return { chain, steps: stepsRef };
}

function chainContainsOrigIdDeep(chain: any[], origId: string): boolean {
  if (!origId || !Array.isArray(chain) || chain.length === 0) return false;

  const visited = new Set<any>();
  const stack = [...chain];

  while (stack.length) {
    const step = stack.pop();
    // eslint-disable-next-line
    if (!step || visited.has(step)) continue;
    visited.add(step);

    if (step?.origId === origId) return true;

    if (Array.isArray(step?.routes)) {
      step.routes.forEach((route: any) => {
        if (Array.isArray(route?.steps) && route.steps.length) {
          stack.push(...route.steps);
        }
      });
    }
  }

  return false;
}

function flushPendingChainAttaches(): void {
  if (pendingChainAttachKeys.size === 0) return;

  const keys = Array.from(pendingChainAttachKeys);
  keys.forEach((key) => {
    const [source, target] = key.split(">>");
    if (!source || !target) {
      pendingChainAttachKeys.delete(key);
      return;
    }

    const edgeStillExists = edges.value.some(
      (e) => e?.source === source && e?.target === target && !e?.hidden
    );
    if (!edgeStillExists) {
      pendingChainAttachKeys.delete(key);
      return;
    }

    const sourceStepResult = findStepByOrigId(stepList.value, source);
    const targetStepResult = findStepByOrigId(stepList.value, target);
    if (!sourceStepResult?.step || !targetStepResult?.step) {
      pendingChainAttachKeys.delete(key);
      return;
    }

    const { chain: chainCandidates } = buildConnectedChainSteps(
      targetStepResult.steps,
      targetStepResult.index
    );
    const chainIds = chainCandidates.map((s) => s?.origId).filter(Boolean);
    const chainSet = new Set(chainIds);

    const sourceInChain = chainSet.has(source);
    const chainContainsSourceDeep = chainContainsOrigIdDeep(
      chainCandidates,
      source
    );
    const hasExternalIncoming = edges.value.some((edge) => {
      if (edge?.hidden) return false;
      if (edge?.source === source && edge?.target === target) return false;
      return chainSet.has(edge.target) && !chainSet.has(edge.source);
    });

    const shouldAttachDetachedChain =
      chainIds.length > 0 &&
      !sourceInChain &&
      !chainContainsSourceDeep &&
      !hasExternalIncoming;

    if (!shouldAttachDetachedChain) return;

    const detachedChain = detachChainFromList(
      targetStepResult.steps,
      chainCandidates
    );
    if (!detachedChain.length) {
      pendingChainAttachKeys.delete(key);
      return;
    }

    if (
      sourceStepResult.step?.type === "ROUTER" &&
      !targetStepResult.step?.isRouterConnection
    ) {
      attachChainToNewRoute(sourceStepResult.step, detachedChain);
    } else {
      attachChainAfterSource({
        chain: detachedChain,
        destinationSteps: sourceStepResult.steps,
        sourceOrigId: sourceStepResult.step.origId,
        parentId: sourceStepResult.step?.parentId,
      });
    }

    pendingChainAttachKeys.delete(key);
  });
}

function edgeMatchesConnection(edge: any, source: string, target: string) {
  return edge?.source === source && edge?.target === target;
}

function hasExternalIncomingEdges(
  chainIds: string[],
  ignoredConnection?: { source: string; target: string } | null,
  edgesList: Edge[] = edges.value
) {
  if (!Array.isArray(chainIds) || chainIds.length === 0) return false;

  const chainSet = new Set(chainIds);
  return edgesList.some(
    (edge) =>
      !(
        ignoredConnection &&
        edgeMatchesConnection(
          edge,
          ignoredConnection.source,
          ignoredConnection.target
        )
      ) &&
      chainSet.has(edge.target) &&
      !chainSet.has(edge.source) &&
      !edge.hidden
  );
}

function detachChainFromList(steps: any[], chain: any[]) {
  if (!Array.isArray(steps) || !Array.isArray(chain) || !chain.length) {
    return [];
  }

  const chainIds = new Set(chain.map((step) => step?.origId).filter(Boolean));
  if (!chainIds.size) return [];

  const remaining = steps.filter((step) => !chainIds.has(step?.origId));
  steps.splice(0, steps.length, ...remaining);

  return chain.filter((step) => chainIds.has(step?.origId));
}

function attachChainAfterSource({
  chain,
  destinationSteps,
  sourceOrigId,
  parentId,
}: {
  chain: any[];
  destinationSteps: any[];
  sourceOrigId: string;
  parentId?: string;
}) {
  if (!Array.isArray(chain) || !chain.length) return;
  if (!Array.isArray(destinationSteps)) return;

  const insertIndex = destinationSteps.findIndex(
    (step: any) => step?.origId === sourceOrigId
  );

  if (insertIndex === -1) return;

  chain.forEach((step) => {
    if (parentId) {
      step.parentId = parentId;
    } else {
      delete step.parentId;
    }

    if (step?.isRouterConnection) {
      delete step.isRouterConnection;
    }

    const node = nodes.value.find((n: any) => n?.data?.origId === step?.origId);
    if (node?.data) {
      if (parentId) {
        node.data.parentId = parentId;
      } else {
        delete node.data.parentId;
      }
      if (node.data.isRouterConnection) {
        delete node.data.isRouterConnection;
      }
    }
  });

  destinationSteps.splice(insertIndex + 1, 0, ...chain);
}

function reconcileVisibleEdgeAttachments(edgesList: Edge[]) {
  if (!Array.isArray(edgesList) || !edgesList.length) return false;

  let changed = false;

  edgesList.forEach((edge: any) => {
    if (!edge || edge.hidden) return;
    if (edge.source === "start") return;
    if (isLoopBackEdge(edge)) return;

    const source = normalizeRemovalEdgeValue(edge.source);
    const target = normalizeRemovalEdgeValue(edge.target);
    const sourceHandle = normalizeRemovalEdgeValue(edge.sourceHandle);
    const targetHandle = normalizeRemovalEdgeValue(edge.targetHandle);

    if (!source || !target) return;
    if (!sourceHandle.includes("__handle-right")) return;
    if (!targetHandle.includes("__handle-left")) return;

    const sourceStepResult = findStepByOrigId(stepList.value, source);
    const targetStepResult = findStepByOrigId(stepList.value, target);

    if (!sourceStepResult?.step || !targetStepResult?.step) return;
    if (!Array.isArray(sourceStepResult.steps)) return;
    if (!Array.isArray(targetStepResult.steps)) return;
    if (sourceStepResult.steps === targetStepResult.steps) return;

    const { chain: chainCandidates } = buildConnectedChainSteps(
      targetStepResult.steps,
      targetStepResult.index,
      edgesList
    );
    const chainIds = chainCandidates
      .map((step) => step?.origId)
      .filter(Boolean);

    if (!chainIds.length) return;
    if (chainIds.includes(source)) return;
    if (chainContainsOrigIdDeep(chainCandidates, source)) return;
    if (hasExternalIncomingEdges(chainIds, { source, target }, edgesList)) {
      return;
    }

    const detachedChain = detachChainFromList(
      targetStepResult.steps,
      chainCandidates
    );
    if (!detachedChain.length) return;

    attachChainAfterSource({
      chain: detachedChain,
      destinationSteps: sourceStepResult.steps,
      sourceOrigId: sourceStepResult.step.origId,
      parentId: sourceStepResult.step?.parentId,
    });
    changed = true;
  });

  return changed;
}

function isPlainReconnectStep(step: any) {
  return (
    step &&
    !step.type &&
    !checkIsLoop(step) &&
    !checkIsLoopEnd(step) &&
    !checkIsLoopStep(step)
  );
}

function prepareDetachedChainForReconnect(
  sourceOrigId: string,
  targetOrigId: string,
  sourceHandle = "right",
  targetHandle = "left"
) {
  if (sourceHandle !== "right" || targetHandle !== "left") return;

  const sourceStepResult = findStepByOrigId(stepList.value, sourceOrigId);
  const targetStepResult = findStepByOrigId(stepList.value, targetOrigId);
  const sourceParentId = sourceStepResult?.step?.parentId;

  if (!sourceStepResult?.step || !targetStepResult?.step || !sourceParentId) {
    return;
  }

  if (sourceStepResult.steps === targetStepResult.steps) return;
  if (targetStepResult.steps !== stepList.value) return;
  if (targetStepResult.step?.parentId) return;
  if (!isPlainReconnectStep(targetStepResult.step)) return;

  const { chain } = buildConnectedChainSteps(
    targetStepResult.steps,
    targetStepResult.index
  );
  const chainIds = chain.map((step) => step?.origId).filter(Boolean);
  const chainSet = new Set(chainIds);

  if (!chainIds.length) return;
  if (!chain.every(isPlainReconnectStep)) return;
  if (chainSet.has(sourceOrigId)) return;
  if (chainContainsOrigIdDeep(chain, sourceOrigId)) return;

  const hasBlockingIncoming = edges.value.some((edge) => {
    if (edge?.hidden) return false;
    if (edge?.source === sourceOrigId && edge?.target === targetOrigId) {
      return false;
    }

    return chainSet.has(edge.target) && !chainSet.has(edge.source);
  });

  if (hasBlockingIncoming) return;

  const detachedChain = detachChainFromList(targetStepResult.steps, chain);
  attachChainAfterSource({
    chain: detachedChain,
    destinationSteps: sourceStepResult.steps,
    sourceOrigId,
    parentId: sourceParentId,
  });
}

function attachChainToNewRoute(routerStep: any, chain: any[]) {
  if (!routerStep || !Array.isArray(chain) || !chain.length) return;

  const routeId = uuidv4();
  const routeOrigId = uuidv4();

  const routeSteps = chain.map((step, index) => {
    const updatedStep = step;
    updatedStep.parentId = routeId;
    updatedStep.isRouterConnection = index === 0;

    if (index === 0) {
      updatedStep.id = uniqueId("router-step");
    }

    return updatedStep;
  });

  routerStep.routes = Array.isArray(routerStep.routes) ? routerStep.routes : [];

  routerStep.routes.push({
    id: routeId,
    parentId: routerStep.id,
    origId: routeOrigId,
    steps: routeSteps,
    name: `Condition ${routerStep.routes.length + 1}`,
    filter: null,
  });
}

function isEdgeValidForStepList(item: any, validOrigIds: Set<string>) {
  const source = item?.source;
  const target = item?.target;

  if (typeof source !== "string" || !source) return false;
  if (typeof target !== "string" || !target) return false;
  if (source !== "start" && !validOrigIds.has(source)) return false;
  if (!validOrigIds.has(target)) return false;
  if (source === "start" && !canStartConnectToOrigId(target)) return false;

  const sourceHandle =
    typeof item?.sourceHandle === "string" ? item.sourceHandle : "";
  const targetHandle =
    typeof item?.targetHandle === "string" ? item.targetHandle : "";
  if (sourceHandle.startsWith("undefined__handle-")) return false;
  if (targetHandle.startsWith("undefined__handle-")) return false;

  return true;
}

function removeLoopStepUiData(steps, currentStep) {
  steps.forEach((step) => {
    if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
      if (step.origId === currentStep.origId) {
        delete step.uiData;
      }
    } else if (step.routes) {
      step.routes.forEach((route) => {
        removeLoopStepUiData(route.steps, currentStep);
      });
    }
  });
}

function findStepById(steps, id) {
  let result = null;
  steps.forEach((step, index) => {
    if (result) return;
    if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
      if (step.id === id) {
        result = { steps, index, step };
      }
    } else if (step.routes) {
      step.routes.forEach((route) => {
        // Added chaining
        if (result) return;
        const res = findStepById(route.steps, id);
        if (res) result = res;
      });
    }
  });
  return result;
}

function findRouteById(steps, id) {
  let result = null;
  if (id) {
    steps.forEach((step, i) => {
      if (step.id === id) {
        result = { steps, index: i, step }; // Return the matching step, its index, and its parent steps
      } else if (step.routes) {
        step.routes.forEach((route, j) => {
          if (route.id === id) {
            // Return the matching route, its index, and its parent routes
            result = { steps: step.routes, index: j, route };
          } else {
            const found = findRouteById(route.steps, id); // Recursively search in nested steps
            if (found) {
              result = found; // Return the found route if id is found within nested steps
            }
          }
        });
      }
    });
  }

  return result;
}

function updateRouteNameLabel(routeId: string, name: string) {
  const route = findRouteById(stepList.value, routeId);
  if (route?.route) {
    route.route.name = name;
  }
}

// this function is used to make the default pattren positions for steps
// as initially there is not position available for all the prevs steps
// MATH.random function is used for a bug that makes lines disappears when the both nodes get same x position
// so we are adding a random number to the x position of the node
function getPosition(axis, index) {
  return axis * 180 + (index + Math.random() + Math.random()) / 100;
}

async function updateNodes(steps: any[]) {
  await nextTick();
  syncLoopUiData(
    stepList.value,
    edges.value.length ? edges.value : props.flowLines
  );
  normalizeFlowSkipState(stepList.value);
  const isEditPage = !window.location.href.includes("flows/create");
  const newNodes = [];
  const newEdges = [];
  const routerNodes: Record<number, any> = {};
  let x;
  if (props.startStepPosition !== null) {
    x = props.startStepPosition;
  } else if (isEditPage) {
    x = -200;
  } else {
    x = 0.01;
  }
  // Adding hardcoded start node
  newNodes.push({
    id: "start",
    type: "start",
    label: "Start",
    position: { x, y: 0.01 },
    data: { id: "start", origId: "start" },
  });
  const processSteps = (stepsList: any[], parentIndex: number) => {
    stepsList.forEach((item: any, index: number, stepsArray: any[]) => {
      // Restore router insideLoop properties from uiData
      if (item?.type === "ROUTER" && item.uiData && !item.insideLoop) {
        const uiData = parseUiData(item.uiData);
        if (uiData?.insideLoop) {
          item.insideLoop = true;
          item.parentLoopOrigId = uiData.parentLoopOrigId;
          item.loopEndOrigId = uiData.loopEndOrigId;
        }
      }

      const nodeExists = newNodes.some(
        (node) => node.id === String(item.origId)
      );
      if (nodeExists) {
        return;
      }
      const router = findRouteById(stepList.value, item?.parentId);

      const currentIndex = newNodes.length;
      const sameIxRouter = routerNodes[item.ix];
      const defaultX = getPosition(
        item.ix ?? item.ix !== undefined ? item.ix + 1 : index + 1,
        index
      );
      const defaultY = getPosition(
        item.iy ?? item.iy !== undefined ? item.iy : index,
        index
      );
      let positionX = defaultX;
      let positionY = defaultY;
      if (item?.position?.x !== undefined) {
        positionX =
          item.position.x === item.ix
            ? getPosition(item.ix, index) + Math.random()
            : item.position.x;
      }
      if (item?.position?.y !== undefined) {
        positionY =
          item.position.y === item.y
            ? getPosition(item.iy, index)
            : item.position.y;
      }
      if (item?.type === "ROUTER" && item.routes.length > 0) {
        newNodes.push({
          id: String(item.origId),
          type: "router",
          label: String(index),
          position: {
            x: positionX,
            y: positionY,
          },
          data: { ...item },
        });
        item.position = {
          x: positionX,
          y: positionY,
        };
        routerNodes[item.ix] = newNodes[newNodes.length - 1]; // Track the router node

        if (stepsArray[index - 1]) {
          newEdges.push({
            id: `e${item.origId}-${stepsArray[index - 1].origId}`,
            source: stepsArray[index - 1].origId,
            target: item.origId,
            type: "custom",
            label: String(index),
            data: { ...stepsArray[index - 1] }, // Capture previous step's data
            animated: index === 0 && parentIndex > 0,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 7,
              color: DEFAULT_MARKER_COLOR,
            },
          });
        }

        item.routes.forEach((route: any) => {
          processSteps(route.steps, currentIndex);
        });
      } else if (item?.type === "LOOP_END") {
        // Handle LOOP_END node
        newNodes.push({
          id: String(item.origId),
          type: "loopEnd",
          label: String(index),
          draggable: !props.nodesDraggable ? false : !item?.lock,
          position: {
            x: positionX,
            y: positionY,
          },
          data: { ...item },
        });
        item.position = {
          x: positionX,
          y: positionY,
        };

        // Add edge from previous step
        const prevStep = stepsArray[index - 1];
        if (prevStep?.origId) {
          newEdges.push({
            id: `e${item.origId}-${prevStep.origId}`,
            source: prevStep.origId,
            target: item.origId,
            animated: false,
            type: "custom",
            label: String(index),
            data: { ...prevStep },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 7,
              color: DEFAULT_MARKER_COLOR,
            },
          });
        }
      } else {
        newNodes.push({
          id: String(item.origId),
          type: "custom",
          label: String(index),
          draggable: !props.nodesDraggable ? false : !item?.lock,
          position: {
            x: positionX,
            y: positionY,
          },
          // position: item.position,
          data: { ...item },
        });
        item.position = {
          x: positionX,
          y: positionY,
        };
        if (sameIxRouter && index === 0) {
          newEdges.push({
            id: `e${item.origId}-${sameIxRouter.data.origId}`,
            source: sameIxRouter.data.origId,
            target: item.origId,
            type: "custom",
            label: String(index),
            data: {
              ...sameIxRouter.data,
              conditionName: router?.route?.name,
              stepIndex: router?.index,
            },
            sourceHandle: `${sameIxRouter.data.origId}__handle-bottom`,
            targetHandle: `${item.origId}__handle-top`,
            animated: true, // Set to true if animation is needed
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 7,
              color: DEFAULT_MARKER_COLOR,
            },
          });
        } else {
          const animated = false;
          const step = stepsArray[index - 1];
          if (!item?.isCopy && step?.origId) {
            newEdges.push({
              id: `e${item.origId}-${step.origId}`,
              source: step.origId,
              target: item.origId,
              animated,
              type: "custom",
              label: String(index),
              data: { ...newNodes[currentIndex - 1].data },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 7,
                color: DEFAULT_MARKER_COLOR,
              },
              sourceHandle: `${step.origId}${
                step?.type === "ROUTER" ? "__handle-bottom" : "__handle-right"
              }`,
              targetHandle: `${item.origId}${
                step?.type === "ROUTER" ? "__handle-top" : "__handle-left"
              }`,
            });
            item.position = {
              x: positionX,
              y: positionY,
            };
          }
        }
      }
    });
  };

  processSteps(steps, 0);

  if (
    canStartConnectToOrigId(steps[0]?.origId) &&
    !hasOnlyDisconnectedSourceLinks(steps[0])
  ) {
    newEdges.push({
      id: `e-start-${steps[0]?.origId}`,
      type: "custom",
      source: "start",
      target: steps[0]?.origId,
      data: {},
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 7,
        color: DEFAULT_MARKER_COLOR,
      },
    });
  }
  nodes.value = newNodes;
  const validOrigIds = collectAllOrigIds(stepList.value);
  const lines = Array.isArray(props.flowLines)
    ? cloneDeep(props.flowLines)
    : [];
  if (edges.value.length === 0) {
    if (lines.length) {
      const validLines = lines.filter((item) =>
        isEdgeValidForStepList(item, validOrigIds)
      );

      if (validLines.length !== lines.length) {
        const dropped = lines
          .filter((e) => !validLines.includes(e))
          .map((e) => e?.id)
          .filter(Boolean);
        console.warn("FlowDiagram: dropped invalid flowLines edges", dropped);
      }

      edges.value = validLines.map((item) => {
        // Parse data if string
        let data = {};
        try {
          data =
            typeof item.data === "string"
              ? JSON.parse(item.data)
              : item.data || {};
        } catch {
          data = {};
        }

        const isHidden = item.hidden || data.hidden || false;
        const markerColor = isHidden ? "transparent" : DEFAULT_MARKER_COLOR;

        return {
          ...item,
          data,
          hidden: isHidden,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: isHidden ? 0 : 7,
            color: markerColor,
          },
        };
      });
      // Validate and fix hidden edges(router exit -> loop end) after loading
      syncAllRouterExitConnections(stepList.value, edges.value);
    } else {
      edges.value = newEdges;
    }
  } else {
    const validEdges = edges.value.filter((item) =>
      isEdgeValidForStepList(item, validOrigIds)
    );
    if (validEdges.length !== edges.value.length) {
      edges.value = validEdges;
    }
  }
  updateNodeList(edges.value);
  stepList.value = updateStepList(stepList.value);
  syncSkipStateForCurrentFlow(stepList.value);
}

function findAndPushStepById(stepsList, id, stepToAdd) {
  const index = stepsList.findIndex((step) => step.id === id);

  if (index !== -1) {
    stepsList.splice(index + 1, 0, stepToAdd);
    return;
  }

  function recursiveSearch(steps) {
    let foundSteps = null;
    steps.some((step) => {
      if (step.id === id) {
        foundSteps = steps;
        return true; // Breaks the loop
      }
      if (step.routes) {
        step.routes.some((route) => {
          foundSteps = recursiveSearch(route.steps);
          return !!foundSteps; // Breaks if steps are found
        });
      }
      return foundSteps; // Continues the loop if steps not found
    });
    return foundSteps;
  }

  stepsList.forEach((item) => {
    if (item.routes) {
      item.routes.some((route) => {
        const foundSteps = recursiveSearch(route.steps);
        if (foundSteps) {
          const stepIndex = foundSteps.findIndex((step) => step.id === id);
          foundSteps.splice(stepIndex + 1, 0, stepToAdd);
          return true; // Breaks the loop after adding
        }
        return false;
      });
    }
  });
}
function splitUniqueId(id) {
  return typeof id === "string" ? id.split("-") : id;
}
function updateUniqueId(stepUniqueId) {
  let updatedUniqueId;

  if (Array.isArray(stepUniqueId)) {
    const lastIndex = stepUniqueId.length - 1;
    stepUniqueId[lastIndex] = (
      parseInt(stepUniqueId[lastIndex], 10) + 1
    ).toString();
    updatedUniqueId = stepUniqueId.join("-");
  } else {
    updatedUniqueId = (parseInt(stepUniqueId, 10) + 1).toString();
  }

  return updatedUniqueId;
}

function handleAddNode(step) {
  const { index, steps } = findStepById(stepList.value, step.id);
  const origId = uuidv4();
  const currentX =
    typeof step?.position?.x === "number"
      ? step.position.x
      : (step?.ix ?? 0) * STRUCTURE_HORIZONTAL_GAP;
  const newXPosition = currentX + STRUCTURE_HORIZONTAL_GAP;

  let steptoAdd: any = {
    id: `new-step-${uuidv4()}`,
    ix: step.ix + 1,
    origId,
    skipMissingIncomingChain: true,
    position: {
      x: newXPosition,
      y: step.position?.y + Math.random() / 10,
    },
  };
  assignStepNumber(steptoAdd, props.flow);

  // Handle step inside a condition (router route)
  if (step?.parentId) {
    steptoAdd = { ...steptoAdd, parentId: step.parentId };
  } else if (checkIsLoopEnd(step)) {
    // Handle LOOP_END that might be inside a router condition but lacks parentId
    const loopStartOrigId = getLoopStartOrigId(step);
    if (loopStartOrigId) {
      const loopStart = findStepByOrigId(stepList.value, loopStartOrigId);
      if (loopStart?.step?.parentId) {
        steptoAdd.parentId = loopStart.step.parentId;
      }
      // If this is nested-loop new step after LOOP_END should be part of the parent loop steps
      const parentLoopOrigId = getParentLoopOrigId(loopStart?.step);
      if (parentLoopOrigId) {
        const parentLoop = findStepByOrigId(stepList.value, parentLoopOrigId);
        if (parentLoop?.step) {
          steptoAdd.uiData = createLoopStepUiData(parentLoopOrigId);
          updateLoopStep(
            stepList.value,
            steptoAdd,
            getLoopStepCount(parentLoop.step),
            "add"
          );
        }
      }
    }
  }
  const loopContext = getLoopContextForStep(step, stepList.value);

  // Handle loop-aware step context
  if (!checkIsLoopEnd(step) && loopContext.inLoop && loopContext.loopOrigId) {
    steptoAdd = {
      ...steptoAdd,
      uiData: createLoopStepUiData(loopContext.loopOrigId),
    };

    const parentLoop = findStepByOrigId(stepList.value, loopContext.loopOrigId);

    if (parentLoop?.step) {
      updateLoopStep(
        stepList.value,
        steptoAdd,
        getLoopStepCount(parentLoop.step),
        "add"
      );
    }
  }

  findAndPushStepById(stepList.value, step.id, steptoAdd);
  applyScopedHorizontalAddShift({
    insertedRange: {
      startIndex: index + 1,
      endIndex: index + 1,
    },
    targetList: steps,
    anchorOrigId: step.origId,
  });

  let stepUniqueId = splitUniqueId(step.uniqueId);
  stepUniqueId = updateUniqueId(stepUniqueId);
  stepUniqueId =
    stepUniqueId.length > 1 ? stepUniqueId : parseInt(stepUniqueId, 10);

  const addedStep = {
    ix: step.ix + 1,
    iy: step.iy,
    id: steptoAdd.id,
    uniqueId: stepUniqueId,
  };
  addStepEdge(step.origId, origId);
  syncAndEmitUpdate({
    extra: { addedStep },
    initialLoad: false,
    list: stepList.value,
    edgesArr: edges.value,
    onlyLineUpdate: false,
  });
  selectStepAfterAdd(origId, "node");
}

function updateLoopStep(steps, currentStep, index, type) {
  steps.forEach((step) => {
    if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
      step.uiData = handleLoopStep(step, currentStep, index, type);
    } else if (step.routes) {
      step.routes.forEach((route) => {
        updateLoopStep(route.steps, currentStep, index, type);
      });
    }
  });
}

function handleLoopStep(step, currentStep, index, type: "add" | "remove") {
  if (!checkIsLoop(step)) {
    return step.uiData;
  }

  const loopOrigId = step.origId;

  // Check if currentStep's uiData indicates this loop is its parent
  const currentStepParentLoopId = getParentLoopOrigId(currentStep);
  if (currentStepParentLoopId && currentStepParentLoopId === loopOrigId) {
    return updateLoopStepsInUiData(
      step.uiData,
      currentStep.origId,
      index,
      type
    );
  }

  return step.uiData;
}

function handleAddnodeAfterStart() {
  const origId = uuidv4();
  const firstRootStep = stepList.value[0] ?? null;
  const previousStartTargetFromEdge = edges.value.find(
    (edge) => !edge.hidden && edge.source === "start"
  )?.target;
  const previousStartTarget = previousStartTargetFromEdge ?? null;
  const newXPosition =
    typeof firstRootStep?.position?.x === "number"
      ? firstRootStep.position.x
      : STRUCTURE_HORIZONTAL_GAP;
  const newYPosition =
    typeof firstRootStep?.position?.y === "number"
      ? firstRootStep.position.y
      : 0;
  const newStep: any = {
    id: `new-step-${uuidv4()}`,
    origId,
    ix: 0,
    iy: 0,
    skipMissingIncomingChain: true,
    position: {
      x: newXPosition,
      y: newYPosition + Math.random() / 10,
    },
  };
  assignStepNumber(newStep, props.flow);
  stepList.value.splice(0, 0, newStep);
  applyScopedHorizontalAddShift({
    insertedRange: {
      startIndex: 0,
      endIndex: 0,
    },
    targetList: stepList.value,
    anchorOrigId: "start",
  });
  const addedStep = {
    ix: newStep.ix,
    iy: newStep.iy,
    id: newStep.id,
    uniqueId: newStep.uniqueId ?? 0,
  };
  edges.value = edges.value.filter(
    (edge) => edge.hidden || edge.source !== "start"
  );
  addSimpleEdge("start", origId, "right", "left", false);
  if (
    typeof previousStartTarget === "string" &&
    previousStartTarget &&
    previousStartTarget !== origId
  ) {
    addSimpleEdge(origId, previousStartTarget, "right", "left", false);
  }
  syncAndEmitUpdate({
    extra: { addedStep },
    initialLoad: false,
    list: stepList.value,
    edgesArr: edges.value,
    onlyLineUpdate: false,
  });
  selectStepAfterAdd(origId, "node");
}

function shouldKeepAdjacentLoopPlaceholder(
  steps: any[],
  stepToRemove: any,
  stepIndex: number
) {
  const previousStep = steps[stepIndex - 1];
  const nextStep = steps[stepIndex + 1];
  const isOnlyStepBetweenLoopBoundaries =
    checkIsLoop(previousStep) && checkIsLoopEnd(nextStep);

  if (!isOnlyStepBetweenLoopBoundaries) return false;

  if (stepToRemove?.type !== "ROUTER") return true;

  const previousLoopOrigId = previousStep?.origId;
  const routerLoopContext = getRouterLoopContext(stepToRemove);
  const routerParentLoopOrigId =
    routerLoopContext?.parentLoopOrigId ??
    stepToRemove?.parentLoopOrigId ??
    getParentLoopOrigId(stepToRemove);

  return Boolean(
    previousLoopOrigId && routerParentLoopOrigId === previousLoopOrigId
  );
}

function findAndRemoveStepById(
  steps,
  id,
  index,
  parentId,
  origId,
  position,
  isStepClear = false
) {
  let stepRemoved = false;

  steps.some((step, i) => {
    // Check if the current step has the target ID
    if (step.id === id) {
      if (
        shouldKeepAdjacentLoopPlaceholder(steps, step, i) ||
        (checkIsLoop(steps[i + 1]) && i === 0) ||
        (steps[i + 1]?.type === "ROUTER" && i === 0) ||
        isStepClear
      ) {
        step.name = "";
        step.connection = undefined;
        step.actions = undefined;
        if (step.type === "ROUTER") {
          const placeholderNode = nodes.value.find(
            (node) => node?.data?.origId === step.origId
          );
          delete step.type;
          delete step.routes;
          delete step.insideLoop;
          delete step.parentLoopOrigId;
          delete step.loopEndOrigId;
          if (checkIsLoop(steps[i - 1])) {
            step.uiData = createLoopStepUiData(steps[i - 1].origId);
          }
          if (placeholderNode) {
            placeholderNode.type = "custom";
            placeholderNode.data = { ...placeholderNode.data, ...step };
            delete placeholderNode.data.type;
            delete placeholderNode.data.routes;
            delete placeholderNode.data.insideLoop;
            delete placeholderNode.data.parentLoopOrigId;
            delete placeholderNode.data.loopEndOrigId;
          }
        }
        stepRemoved = false;
      } else {
        steps.splice(i, 1);
        stepRemoved = true;
      }
      return stepRemoved; // Break out of the loop after removal
    }

    // If the step has routes, iterate through them
    if (step.routes) {
      step.routes.some((route) => {
        // Recursively search and remove the step by ID in the nested routes
        const result = findAndRemoveStepById(
          route.steps,
          id,
          index,
          parentId,
          origId,
          position,
          isStepClear
        );
        if (result) {
          // Check if the route steps length is 0 and index is 0 after removal
          if (route.steps.length === 0 && index === 0) {
            const newStep = {
              id: `router-step-${uuidv4()}`,
              parentId,
              origId,
              position: { x: position?.x || null, y: position?.y || null },
            };
            assignStepNumber(newStep, props.flow);

            // Push the new step to the steps list
            route.steps.push(newStep);

            update({
              extra: {
                addedStep: { ix: route?.steps?.[0]?.ix, id: newStep.id },
              },
            });
          }
          stepRemoved = true;
          return true; // Break out after processing the step removal
        }
        return false;
      });
    }

    return stepRemoved;
  });

  return stepRemoved;
}

async function handleRemoveNode({
  id,
  step,
  index,
  parentId,
}: {
  id: string;
  step: any;
  index?: number;
  parentId?: string;
}) {
  const currentStep = findStepById(stepList.value, id);
  const { rightStep, leftStep } = siblingSteps(step?.origId);
  const hadStartConnection = edges.value.some(
    (edge) =>
      edge.source === "start" && edge.target === step.origId && !edge.hidden
  );
  const removalStepResult = findStepByOrigId(stepList.value, step?.origId);
  const removedStructureBounds =
    removalStepResult &&
    getStepsBounds(
      collectStructureSteps(
        removalStepResult.steps,
        getStructureRange(removalStepResult.steps, removalStepResult.index)
      )
    );

  if (props.selectedIndex !== undefined) {
    await emit("close-drawer");
  }

  const routerRemovalContext =
    step.type === "ROUTER" ? getRouterLoopContext(step) : null;
  const routerRemovalLoopEnd = routerRemovalContext?.insideLoop
    ? findStepByOrigId(stepList.value, routerRemovalContext.loopEndOrigId)
    : null;

  // Handle ROUTER removal inside a loop
  if (
    step.type === "ROUTER" &&
    routerRemovalContext?.insideLoop &&
    checkIsLoopEnd(routerRemovalLoopEnd?.step)
  ) {
    const routerLoopEndOrigId = routerRemovalLoopEnd.step.origId;
    const keepsLoopPlaceholder = Boolean(
      removalStepResult &&
        shouldKeepAdjacentLoopPlaceholder(
          removalStepResult.steps,
          step,
          removalStepResult.index
        )
    );

    // Remove visible edge from router to LOOP_END
    edges.value = edges.value.filter(
      (e) =>
        !(
          e.source === step.origId &&
          e.target === routerLoopEndOrigId &&
          !e.hidden
        )
    );

    // Remove animated edges from router to condition steps
    edges.value = edges.value.filter(
      (e) => !(e.source === step.origId && e.animated)
    );

    // Collect all step origIds from routes
    const routeStepIds = new Set<string>();
    if (step.routes) {
      step.routes.forEach((route: any) => {
        if (route.steps) {
          route.steps.forEach((s: any) => {
            if (s.origId) routeStepIds.add(s.origId);
          });
        }
      });
    }

    // Remove edges connected to route steps
    edges.value = edges.value.filter((e) => {
      if (!routeStepIds.has(e.source) && !routeStepIds.has(e.target)) {
        return true;
      }
      if (routeStepIds.has(e.source) && routeStepIds.has(e.target)) {
        return false;
      }
      if (routeStepIds.has(e.source)) {
        return false;
      }
      return true;
    });

    // Remove edge TO the router
    edges.value = edges.value.filter((e) => e.target !== step.origId);

    // Reconnect step before router
    if (leftStep?.data?.origId) {
      const leftStepResult = findStepByOrigId(
        stepList.value,
        leftStep.data.origId
      );
      const canReconnectVisible =
        leftStepResult?.steps === routerRemovalLoopEnd.steps;

      if (!keepsLoopPlaceholder && canReconnectVisible) {
        reconnectSteps({
          source: leftStep.data.origId,
          target: routerLoopEndOrigId,
        });
      }
    }

    // Remove the router and all its route steps from stepList
    const isRouterRemoved = findAndRemoveStepById(
      stepList.value,
      id,
      index,
      parentId,
      step.origId,
      step?.position,
      keepsLoopPlaceholder
    );
    if (!isRouterRemoved && keepsLoopPlaceholder && leftStep?.data?.origId) {
      edges.value = edges.value.filter(
        (e) =>
          e.hidden ||
          !(
            e.source === leftStep.data.origId &&
            e.target === routerLoopEndOrigId
          )
      );
      reconnectSteps({
        source: leftStep.data.origId,
        target: step.origId,
      });
      reconnectSteps({
        source: step.origId,
        target: routerLoopEndOrigId,
      });
    }
    if (removalStepResult && removedStructureBounds) {
      compactAfterStructureRemoval(
        removalStepResult.steps,
        removalStepResult.index,
        removedStructureBounds,
        step.origId
      );
    }

    update({
      extra: {
        removedStep: {
          ix: step?.ix,
          id: step?.id,
          iy: step?.iy,
          uniqueId: step?.uniqueId,
          step,
        },
      },
    });
    return;
  }

  // Handle first step case
  if (stepList.value[0]?.id === id && stepList.value.length === 1) {
    findAndRemoveStepById(
      stepList.value,
      id,
      index,
      parentId,
      step.origId,
      step?.position,
      true
    );

    update({
      extra: {
        removedStep: {
          ix: step?.ix,
          id: step?.id,
          iy: step?.iy,
          uniqueId: step?.uniqueId,
          step,
        },
      },
      edgesArr: edges.value,
      onlyLineUpdate: false,
      initialLoad: false,
    });
    return;
  }

  // Handle LOOP removal (removes loop start, all loop steps, and loop end)
  if (checkIsLoop(step)) {
    syncLoopUiData(stepList.value, edges.value);
    const loop = findStepById(stepList.value, step.id);
    if (loop) {
      const { step: loopStep } = loop;
      const loopEndOrigId = getLoopEndOrigId(loopStep);

      // Collect all IDs to remove
      const idsToRemove = new Set<string>([loopStep.origId]);
      if (loopEndOrigId) {
        idsToRemove.add(loopEndOrigId);
      }

      const processedStructureOrigIds = new Set<string>();
      const isSemanticVisibleEdge = (edge: Edge) =>
        isVisibleLoopRemovalEdge(edge);

      const addStepAndNestedIdsToRemove = (origId: string) => {
        if (!origId || processedStructureOrigIds.has(origId)) return;

        processedStructureOrigIds.add(origId);
        idsToRemove.add(origId);

        const memberResult = findStepByOrigId(stepList.value, origId);
        const memberStep = memberResult?.step;
        if (!memberStep) return;

        if (checkIsLoop(memberStep)) {
          const nestedLoopEndOrigId = getLoopEndOrigId(memberStep);
          if (nestedLoopEndOrigId) {
            idsToRemove.add(nestedLoopEndOrigId);
          }
        }

        if (memberStep.type === "ROUTER") {
          collectAllRouterStepOrigIds(memberStep).forEach(
            (routerStepOrigId) => {
              addStepAndNestedIdsToRemove(routerStepOrigId);
            }
          );
        }
      };

      collectVisibleLoopRemovalOrigIds({
        loopOrigId: loopStep.origId,
        loopEndOrigId,
        edges: edges.value,
      }).forEach((origId) => {
        addStepAndNestedIdsToRemove(origId);
      });

      const isLoopEndReachable = (() => {
        if (!loopStep?.origId || !loopEndOrigId) return false;

        const visited = new Set<string>([loopStep.origId]);
        let currentOrigId = loopStep.origId;

        while (currentOrigId) {
          const sourceOrigId = currentOrigId;
          const nextEdge = edges.value.find(
            (edge) =>
              isSemanticVisibleEdge(edge) &&
              edge.source === sourceOrigId &&
              edge.sourceHandle?.includes("__handle-right")
          );
          const nextOrigId =
            typeof nextEdge?.target === "string" ? nextEdge.target : null;

          if (!nextOrigId || visited.has(nextOrigId)) return false;
          if (nextOrigId === loopEndOrigId) return true;

          visited.add(nextOrigId);
          currentOrigId = nextOrigId;
        }

        return false;
      })();
      const incomingLoopEdge = edges.value.find(
        (edge) =>
          isSemanticVisibleEdge(edge) &&
          edge.target === loopStep.origId &&
          edge.targetHandle?.includes("__handle-left")
      );
      const incomingLoopSource =
        typeof incomingLoopEdge?.source === "string"
          ? incomingLoopEdge.source
          : null;
      const incomingLoopSourceStep =
        incomingLoopSource && incomingLoopSource !== "start"
          ? findStepByOrigId(stepList.value, incomingLoopSource)?.step
          : null;
      const incomingLoopSourceExists =
        incomingLoopSource === "start" || Boolean(incomingLoopSourceStep);
      const incomingLoopSourceIsRouterRight =
        incomingLoopSourceStep?.type === "ROUTER" &&
        incomingLoopEdge?.sourceHandle?.includes("__handle-right");
      const loopEndOutgoingEdge =
        isLoopEndReachable && loopEndOrigId
          ? edges.value.find(
              (edge) =>
                isSemanticVisibleEdge(edge) &&
                edge.source === loopEndOrigId &&
                !idsToRemove.has(String(edge.target ?? "")) &&
                edge.sourceHandle?.includes("__handle-right")
            )
          : null;
      const stepAfterLoopEnd = findStepByOrigId(
        stepList.value,
        loopEndOutgoingEdge?.target
      )?.step;

      // Remove all nodes from stepList
      const removeByOrigId = (stepsArr: any[]) => {
        for (let i = stepsArr.length - 1; i >= 0; i--) {
          const s = stepsArr[i];
          if (idsToRemove.has(s.origId)) {
            stepsArr.splice(i, 1);
          } else if (s.routes) {
            s.routes.forEach((route: any) => removeByOrigId(route.steps));
          }
        }
      };

      removeByOrigId(stepList.value);

      // Remove all edges connected to removed nodes
      edges.value = edges.value.filter(
        (edge) => !idsToRemove.has(edge.source) && !idsToRemove.has(edge.target)
      );

      // Reconnect only when the removed loop had a real visible incoming edge.
      if (
        incomingLoopSource &&
        incomingLoopSourceExists &&
        !incomingLoopSourceIsRouterRight &&
        isLoopEndReachable &&
        !idsToRemove.has(incomingLoopSource) &&
        stepAfterLoopEnd?.origId
      ) {
        reconnectSteps({
          source: incomingLoopSource,
          target: stepAfterLoopEnd.origId,
          sourceHandle:
            incomingLoopSource === "start"
              ? "right"
              : getHandleSuffix(
                  incomingLoopEdge?.sourceHandle,
                  incomingLoopSource,
                  "right"
                ),
        });
      }

      // Remove all hidden edges targeting this LOOP_END
      if (loopEndOrigId) {
        edges.value = edges.value.filter(
          (e) => !(e.target === loopEndOrigId && e.hidden)
        );
      }

      if (removalStepResult && removedStructureBounds) {
        compactAfterStructureRemoval(
          removalStepResult.steps,
          removalStepResult.index,
          removedStructureBounds,
          step.origId
        );
      }

      setTimeout(() => {
        syncAndEmitUpdate({
          extra: {
            removedStep: {
              ix: step?.ix,
              id: step?.id,
              iy: step?.iy,
              uniqueId: step?.uniqueId,
              step,
            },
          },
          list: stepList.value,
          edgesArr: edges.value,
          onlyLineUpdate: false,
          initialLoad: false,
        });
      }, 0);
      return;
    }
  }

  // Handle regular step removal
  const isStepLast =
    rightStep === undefined &&
    checkIsLoop(leftStep?.data) &&
    !checkIsLoopStep(step);

  const isStepRemoved = findAndRemoveStepById(
    stepList.value,
    id,
    index,
    parentId,
    step.origId,
    step?.position,
    isStepLast
  );
  const currentStepEdge = edges.value.find((edge) =>
    isVisibleIncomingStepEdge(edge, step.origId)
  );
  const outgoingStepTargetOrigId = getVisibleOutgoingStepTargetOrigId(
    edges.value,
    step.origId
  );
  const sourceStep = findStepByOrigId(stepList.value, currentStepEdge?.source);
  const isLoop = checkIsLoop(sourceStep?.step);
  edges.value = isStepRemoved
    ? [
        ...edges.value.filter(
          (edge) => edge.source !== step.origId && edge.target !== step.origId
        ),
      ]
    : [...edges.value.filter((edge) => edge.source !== step.origId)];
  if (isLoop && !isStepLast) {
    const targetOrigId = resolveStepRemovalReconnectTarget({
      outgoingEdgeTargetOrigId: outgoingStepTargetOrigId,
    });
    if (currentStepEdge?.source && targetOrigId) {
      reconnectSteps({
        source: String(currentStepEdge.source),
        target: targetOrigId,
        sourceHandle: getHandleSuffix(
          currentStepEdge.sourceHandle,
          String(currentStepEdge.source),
          "right"
        ),
        targetHandle: "left",
        mode: "reuse",
        edge: currentStepEdge,
      });
    }
  } else if (currentStep?.step.origId) {
    if (isStepLast) {
      if (leftStep?.data?.origId) {
        reconnectSteps({
          source: leftStep.data.origId,
          target: currentStep.step.origId,
          sourceHandle: "bottom",
          targetHandle: "left",
          mode: "step",
        });
      }
    } else {
      const sourceOrigId = isStepRemoved
        ? leftStep?.data.origId
        : currentStep.step.origId;
      const targetOrigId = rightStep?.data.origId;

      if (sourceOrigId && targetOrigId) {
        reconnectSteps({
          source: sourceOrigId,
          target: targetOrigId,
          mode: "step",
        });
      }
    }
  }

  if (isStepRemoved && removalStepResult && removedStructureBounds) {
    compactAfterStructureRemoval(
      removalStepResult.steps,
      removalStepResult.index,
      removedStructureBounds,
      step.origId
    );
  }

  if (hadStartConnection && rightStep?.data?.origId) {
    const rightStepResult = findStepByOrigId(
      stepList.value,
      rightStep.data.origId
    );
    const isRootRightStep = rightStepResult?.steps === stepList.value;

    if (isRootRightStep) {
      reconnectSteps({
        source: "start",
        target: rightStep.data.origId,
      });
    }
  }

  if (checkIsLoopStep(step)) {
    const stepEdge = edges.value.find(
      (edge) =>
        edge.target === step.origId && edge.targetHandle?.includes("top")
    );
    updateLoopStep(stepList.value, step, index - 2, "remove");
    if (stepEdge) {
      if (checkIsLoopStep(currentStep.steps?.[index - 1])) {
        const currentStepOrgId =
          currentStep.steps[currentStep.index - 1].origId;
        stepEdge.target = currentStepOrgId;
        stepEdge.targetHandle = `${currentStepOrgId}__handle-top`;
      } else {
        edges.value = [
          ...edges.value.filter(
            (edge) => edge.target !== `${step.origId}__handle-top`
          ),
        ];
      }
    }
  }
  update({
    extra: {
      removedStep: {
        ix: step?.ix,
        id: step?.id,
        iy: step?.iy,
        uniqueId: step.uniqueId,
        step,
      },
    },
  });
}

function update(
  {
    list = stepList.value,
    extra = null,
    onlyLineUpdate = false,
    edgesArr = edges.value,
    initialLoad = false,
  }: {
    list?: any;
    extra?: any;
    edgesArr?: any;
    onlyLineUpdate?: boolean;
    initialLoad?: boolean;
  } = {
    list: stepList.value,
    extra: null,
    onlyLineUpdate: false,
  }
) {
  // Sync all router exit connections before emitting update
  syncAllRouterExitConnections(list, edgesArr);
  normalizeFlowSkipState(list);
  if (list === stepList.value) {
    syncNodeSkipDataFromSteps(list);
  }

  const nextExtra = consumePendingLayoutChangedExtra(extra);

  emit("update", {
    list,
    extra: nextExtra,
    onlyLineUpdate,
    edgesArr,
    initialLoad,
  });
}

function addOrRemoveExpressionType(step, type) {
  const { index, steps } = findStepByOrigId(stepList.value, step.origId);
  let baseX = step.position?.x || step.ix * 200;
  baseX += Math.random();
  let baseY = step.position?.y || step.iy * 200;
  baseY += Math.random();

  const isLoopStep = checkIsLoopStep(step);
  const yOffset = isLoopStep ? 250 : 0; // Push nested loops down
  baseY += yOffset;

  const loopStartId = uuidv4();
  const loopEndId = uuidv4();
  const loopStepId = uuidv4();

  // Loop step (the actual step inside the loop)
  const loopStep: any = {
    id: `new-step-${uuidv4()}`,
    origId: loopStepId,
    ix: step.ix + 2,
    position: {
      x: baseX + 400,
      y: baseY + Math.random() / 100,
    },
    uiData: createLoopStepUiData(loopStartId),
    flowId: step.flowId,
    flowVersion: step.flowVersion,
    // Inherit parentId if step is inside a router condition
    ...(step.parentId && { parentId: step.parentId }),
  };

  const loopEnd = {
    id: `loop-end-${uuidv4()}`,
    origId: loopEndId,
    ix: step.ix + 3,
    position: {
      x: baseX + 600,
      y: baseY + Math.random() / 100,
    },
    uiData: createLoopEndUiData(loopStartId),
    name: "Loop End",
    type: "LOOP_END",
    flowId: step.flowId,
    flowVersion: step.flowVersion,
    ...(step.parentId && { parentId: step.parentId }),
  };

  const loopStart = {
    id: loopStartId,
    origId: loopStartId,
    ix: step.ix + 1,
    position: {
      x: baseX + 200,
      y: baseY + Math.random() / 100,
    },
    uiData: createLoopStartUiData(loopEndId, [
      { loopStepIndex: 0, origId: loopStep.origId },
    ]),
    name: "Loop",
    type: "LOOP",
    loopType: "SPLIT",
    loopConfig: getDefaultLoopConfig(),
    flowId: step.flowId,
    flowVersion: step.flowVersion,
    flow: step.flow,
    ...(step.parentId && { parentId: step.parentId }),
  };

  // Assign stepNumbers in visual order: loopStart → loopStep → loopEnd
  assignStepNumber(loopStart, props.flow);
  assignStepNumber(loopStep, props.flow);
  assignStepNumber(loopEnd, props.flow);

  if (type === "LOOP") {
    loopStartStep.value = loopStart.origId;

    // Check if there's a next step in the parent loop
    let nextStep = steps[index + 1];
    // Store the original next step for edge removal - for the case when a nested loop
    // added in between of two inloop step
    const originalNextStep = nextStep;
    const hadEdgeToOriginalNextStep =
      !!originalNextStep?.origId &&
      hasVisibleEdgeBetween(step.origId, originalNextStep.origId);

    if (nextStep && !hadEdgeToOriginalNextStep) {
      nextStep = undefined;
    }

    // If in a nested loop and there's a next step, check if it's the parent loop end
    if (isLoopStep && nextStep) {
      const parentLoopOrigId = getParentLoopOrigId(step);
      if (parentLoopOrigId) {
        const parentLoop = findStepByOrigId(stepList.value, parentLoopOrigId);

        if (parentLoop?.step) {
          const parentLoopEndOrigId = getLoopEndOrigId(parentLoop.step);
          if (nextStep.origId === parentLoopEndOrigId) {
            nextStep = undefined;
          }
        }
      }
    }

    // Handle nested loops
    if (isLoopStep) {
      const parentLoopOrigId = getParentLoopOrigId(step);

      if (!parentLoopOrigId) {
        console.error("Could not find parent loop for nested loop creation");
        return;
      }

      const parentLoop = findStepByOrigId(stepList.value, parentLoopOrigId);

      if (!parentLoop || !parentLoop.step) {
        console.error("Parent loop not found in step list");
        return;
      }

      loopStart.uiData = createLoopStartUiData(
        loopEndId,
        [{ loopStepIndex: 0, origId: loopStep.origId }],
        parentLoop.step.origId
      );

      // Update parent loop's step tracking
      updateLoopStep(
        stepList.value,
        loopStart,
        getLoopStepCount(parentLoop.step),
        "add"
      );
      updateLoopStep(
        stepList.value,
        loopStep,
        getLoopStepCount(parentLoop.step),
        "add"
      );
      updateLoopStep(
        stepList.value,
        loopEnd,
        getLoopStepCount(parentLoop.step),
        "add"
      );
    }

    // Remove old edges from current step BEFORE adding new steps
    // This prevents the old connections from persisting
    if (originalNextStep) {
      edges.value = edges.value.filter(
        (edge) =>
          !(
            edge.source === step.origId &&
            edge.target === originalNextStep.origId
          )
      );
    }

    // Add ALL steps to the step list FIRST (no edges yet)
    findAndPushStepById(stepList.value, step.id, loopStart);
    findAndPushStepById(stepList.value, loopStart.id, loopStep);
    findAndPushStepById(stepList.value, loopStep.id, loopEnd);
    applyScopedHorizontalAddShift({
      insertedRange: {
        startIndex: index + 1,
        endIndex: index + 3,
      },
      targetList: steps,
      anchorOrigId: step.origId,
    });

    // Single update to trigger node creation
    update();
    selectStepAfterAdd(loopStart.origId, "loop");

    // Add ALL edges AFTER nodes are created (in setTimeout)
    setTimeout(() => {
      addSimpleEdge(step.origId, loopStart.origId, "right", "left"); // Edge: step -> loopStart
      addSimpleEdge(loopStart.origId, loopStep.origId, "right", "left"); // Edge: loopStart -> loopStep
      addSimpleEdge(loopStep.origId, loopEnd.origId, "right", "left"); // Edge: loopStep -> loopEnd
      // Edge: loopEnd -> loopStart (loop back - top to top, animated)
      addSimpleEdge(loopEnd.origId, loopStart.origId, "top", "top", true);

      if (nextStep) {
        // Connect loop end to existing next step
        addSimpleEdge(loopEnd.origId, nextStep.origId, "right", "left");
      } else if (isLoopStep) {
        const parentLoopOrigId = getParentLoopOrigId(step);
        const parentLoop = findStepByOrigId(stepList.value, parentLoopOrigId);

        if (parentLoop?.step) {
          const parentLoopEndOrigId = getLoopEndOrigId(parentLoop.step);
          if (parentLoopEndOrigId) {
            const parentLoopEndStep = findStepByOrigId(
              stepList.value,
              parentLoopEndOrigId
            )?.step;
            const sameRouteOrRoot =
              String(parentLoopEndStep?.parentId ?? "") ===
              String(step.parentId ?? "");

            edges.value = edges.value.filter(
              (edge) =>
                !(
                  edge.source === step.origId &&
                  edge.target === parentLoopEndOrigId &&
                  edge.sourceHandle?.includes("right")
                )
            );

            if (sameRouteOrRoot && hadEdgeToOriginalNextStep) {
              addSimpleEdge(
                loopEnd.origId,
                parentLoopEndOrigId,
                "right",
                "left"
              );
            }
          }
        }
      }

      update();
    }, 0);
  }
}

function pasteStep(stepId) {
  const result = findStepById(stepList.value, stepId);
  const { step } = result;
  const newStep = {
    ...props.clonedStep,
    id: `new-step-${uuidv4()}`,
    origId: uuidv4(),
    stepNumber: undefined,
    position: {
      x: step.position?.x ? step.position?.x : (step.ix + 1) * 180,
      y: step.position?.y ? step.position?.y + 100 : step.iy * 180 + 100,
    },
    ix: step.ix,
    // isCopy: true,
  };
  assignStepNumber(newStep, props.flow);
  stepList.value.push(newStep);
  update();
}

function insertBridgeBeforeLoopStart(routeSteps: any[], routeId: string) {
  if (!Array.isArray(routeSteps) || routeSteps.length === 0) return null;

  const firstStep = routeSteps[0];
  if (!firstStep || firstStep.type !== "LOOP") return null;

  const loopStartOrigId = firstStep.origId;
  const bridgeOrigId = uuidv4();
  let baseX = 0;
  if (typeof firstStep.position?.x === "number") {
    baseX = firstStep.position.x;
  } else if (typeof firstStep.ix === "number") {
    baseX = firstStep.ix * 200;
  }

  let baseY = 0;
  if (typeof firstStep.position?.y === "number") {
    baseY = firstStep.position.y;
  } else if (typeof firstStep.iy === "number") {
    baseY = firstStep.iy * 200;
  }

  const bridgeStep = {
    id: `new-step-${uuidv4()}`,
    parentId: routeId,
    origId: bridgeOrigId,
    skipMissingIncomingChain: true,
    position: {
      x: baseX - 200 + Math.random(),
      y: baseY + Math.random(),
    },
  };

  routeSteps.unshift(bridgeStep);
  return { bridgeOrigId, loopStartOrigId };
}

function alignRouteStructures(
  routeSteps: any[],
  startX: number,
  startY: number
) {
  if (!Array.isArray(routeSteps) || routeSteps.length === 0) return;

  let currentIndex = 0;
  let cursorX = startX;

  while (currentIndex < routeSteps.length) {
    const structureRange = getStructureRange(routeSteps, currentIndex);
    const structureSteps = collectStructureSteps(routeSteps, structureRange);
    const bounds = getStepsBounds(structureSteps);

    if (bounds) {
      const deltaX = cursorX - bounds.minX;
      const deltaY = startY - bounds.minY;
      shiftStructureSteps(structureSteps, deltaX, deltaY);
      cursorX = bounds.maxX + deltaX + STRUCTURE_HORIZONTAL_GAP;
    }

    currentIndex = structureRange.endIndex + 1;
  }
}

function addRouter(id) {
  const result = findStepById(stepList.value, id);
  if (!result) {
    console.error("Step not found");
    return;
  }

  const origId = uuidv4();
  const routerStepOrigId = uuidv4();
  const parentId = uuidv4();
  const { steps, index, step } = result;
  const stepBaseX = step.position?.x ?? step.ix * 200;
  const stepBaseY = step.position?.y ?? step.iy * 200;
  const routerBaseX = stepBaseX + 200;
  const routerBaseY = stepBaseY;
  const routeBaseY = stepBaseY + ROUTER_ROUTE_VERTICAL_GAP;

  const loopContext = getLoopContextForStep(step, stepList.value);
  const nextRouter = steps[index + 1];
  const hasConnectedNextRouter =
    nextRouter?.type === "ROUTER" &&
    edges.value.some(
      (edge) =>
        !edge.hidden &&
        edge.source === step.origId &&
        edge.target === nextRouter.origId
    );

  // Prevent adding a second router only while the next router is connected.
  if (hasConnectedNextRouter) {
    return;
  }

  // Case 1: Adding router directly after LOOP_START
  if (checkIsLoop(step)) {
    const loopEndOrigId = getLoopEndOrigId(step);
    if (!loopEndOrigId) {
      console.error("LOOP_END not found for loop");
      return;
    }

    const loopEndIndex = steps.findIndex((s) => s.origId === loopEndOrigId);
    if (loopEndIndex === -1) {
      console.error("LOOP_END not found in steps array");
      return;
    }

    // Move only the currently connected chain between LOOP and LOOP_END.
    const firstStep = steps[index + 1];
    const stepsToMove = hasVisibleEdgeBetween(
      step.origId,
      firstStep?.origId ?? ""
    )
      ? buildConnectedChainSteps(steps, index + 1).chain.filter(
          (item) => steps.indexOf(item) < loopEndIndex
        )
      : [];
    const lastStepToMove = stepsToMove[stepsToMove.length - 1];
    const shouldConnectRouterToLoopEnd = stepsToMove.length
      ? hasVisibleEdgeBetween(lastStepToMove?.origId ?? "", loopEndOrigId)
      : hasVisibleEdgeBetween(step.origId, loopEndOrigId);

    const movedStepOrigIds = collectAllOrigIds(stepsToMove);

    // Remove only edges that connect moved steps to external nodes (like LOOP or LOOP_END)
    // Keep ALL internal edges (including nested router edges)
    edges.value = edges.value.filter((e) => {
      if (e.hidden) return true;

      const sourceIsMoved = movedStepOrigIds.has(e.source);
      const targetIsMoved = movedStepOrigIds.has(e.target);

      // Keep if both are inside moved steps (internal edges)
      if (sourceIsMoved && targetIsMoved) return true;

      // Keep if neither is in moved steps (unrelated edges)
      if (!sourceIsMoved && !targetIsMoved) return true;

      // Remove edges that cross the boundary (moved <-> external)
      return false;
    });

    // Remove edge from LOOP to first step
    edges.value = edges.value.filter(
      (e) => !(e.source === step.origId && e.sourceHandle?.includes("right"))
    );

    // Remove moved steps from current position
    detachChainFromList(steps, stepsToMove);

    // Remove moved steps from loop's tracking (they're now inside router's condition)
    stepsToMove.forEach((movedStep) => {
      updateLoopStep(stepList.value, movedStep, 0, "remove");
    });

    // Prepare condition steps (only top-level, nested routers keep their structure)
    const routeId = uuidv4();
    const conditionSteps =
      stepsToMove.length > 0
        ? stepsToMove.map((item) => ({
            ...item,
            parentId: routeId,
            uiData:
              item.type === "ROUTER" ||
              item.type === "LOOP" ||
              item.type === "LOOP_END" ||
              checkIsLoopStep(item)
                ? item.uiData
                : undefined,
          }))
        : [
            {
              id: `new-step-${uuidv4()}`,
              parentId: routeId,
              origId: routerStepOrigId,
              skipMissingIncomingChain: true,
              position: {
                x: routerBaseX + Math.random(),
                y: routeBaseY + Math.random(),
              },
            },
          ];
    if (stepsToMove.length > 0) {
      alignRouteStructures(conditionSteps, routerBaseX, routeBaseY);
    }

    insertBridgeBeforeLoopStart(conditionSteps, routeId);

    const newRouter = {
      id: parentId,
      origId,
      type: "ROUTER",
      skipMissingIncomingChain: true,
      position: {
        x: routerBaseX + Math.random(),
        y: routerBaseY + Math.random(),
      },
      uiData: createRouterInsideLoopUiData(step.origId, loopEndOrigId),
      routes: [
        {
          id: routeId,
          parentId,
          origId: uuidv4(),
          steps: conditionSteps,
          name: "Condition 1",
          filter: null,
        },
      ],
    };
    // Assign stepNumbers: router first, then condition steps (visual order)
    assignStepNumber(newRouter, props.flow);
    conditionSteps.forEach((cs) => assignStepNumber(cs, props.flow));

    steps.splice(index + 1, 0, newRouter);
    applyScopedHorizontalAddShift({
      insertedRange: {
        startIndex: index + 1,
        endIndex: index + 1,
      },
      targetList: steps,
      anchorOrigId: step.origId,
    });

    // Register router in parent loop's step tracking
    updateLoopStep(stepList.value, newRouter, getLoopStepCount(step), "add");

    const loopOrigId = step.origId;
    const routerOrigId = origId;
    const firstConditionStepOrigId = conditionSteps[0].origId;

    // LOOP → ROUTER
    addSimpleEdge(loopOrigId, routerOrigId, "right", "left", false);
    // ROUTER → first condition step
    addSimpleEdge(
      routerOrigId,
      firstConditionStepOrigId,
      "bottom",
      "top",
      true
    );
    // ROUTER → LOOP_END (visual)
    if (shouldConnectRouterToLoopEnd) {
      addSimpleEdge(routerOrigId, loopEndOrigId, "right", "left", false);
    }

    // Reconnect internal chain of top-level condition steps
    for (let i = 0; i < conditionSteps.length - 1; i++) {
      const currentOrigId = conditionSteps[i].origId;
      const nextOrigId = conditionSteps[i + 1].origId;

      if (!hasVisibleEdgeBetween(currentOrigId, nextOrigId)) {
        addSimpleEdge(currentOrigId, nextOrigId);
      }
    }

    update();

    setTimeout(() => {
      selectStepAfterAdd(origId, "router");
    }, 0);

    return;
  }

  // Case 2: Last step in array
  if (index === steps.length - 1) {
    const insertedRouterIndex = steps.length;
    const routeId = uuidv4();
    const newRouter: any = {
      id: parentId,
      origId,
      type: "ROUTER",
      skipMissingIncomingChain: true,
      position: {
        x: routerBaseX + Math.random(),
        y: routerBaseY + Math.random(),
      },
      // Propagate parentId from the current step for the nested condition case
      ...(step.parentId && { parentId: step.parentId }),
      routes: [
        {
          id: routeId,
          parentId,
          origId: uuidv4(),
          steps: [
            {
              id: `new-step-${uuidv4()}`,
              parentId: routeId,
              origId: routerStepOrigId,
              skipMissingIncomingChain: true,
              position: {
                x: routerBaseX + Math.random(),
                y: routeBaseY + Math.random(),
              },
            },
          ],
          name: "Condition 1",
          filter: null,
        },
      ],
    };
    // Assign stepNumbers: router first, then condition step (visual order)
    assignStepNumber(newRouter, props.flow);
    assignStepNumber(newRouter.routes[0].steps[0], props.flow);

    // Store loop context on router if inside a loop
    if (loopContext.inLoop && loopContext.loopEndOrigId) {
      newRouter.uiData = createRouterInsideLoopUiData(
        loopContext.loopOrigId,
        loopContext.loopEndOrigId
      );

      // Only register router in parent loop's step tracking if NOT nested inside another router's condition
      // Nested routers are already contained within their parent router which is tracked in the loop
      if (!step.parentId) {
        const parentLoop = findStepByOrigId(
          stepList.value,
          loopContext.loopOrigId
        );
        if (parentLoop?.step) {
          updateLoopStep(
            stepList.value,
            newRouter,
            getLoopStepCount(parentLoop.step),
            "add"
          );
        }
      }
    }

    steps.push(newRouter);
    applyScopedHorizontalAddShift({
      insertedRange: {
        startIndex: insertedRouterIndex,
        endIndex: insertedRouterIndex,
      },
      targetList: steps,
      anchorOrigId: step.origId,
    });

    // Visual edge: Router → first step in condition (animated)
    addSimpleEdge(origId, routerStepOrigId, "bottom", "top", true);

    // If inside loop, add connections to LOOP_END
    if (
      loopContext.inLoop &&
      loopContext.loopEndOrigId &&
      !isNestedInsideRouterInSameLoop(
        step,
        stepList.value,
        loopContext.loopOrigId,
        loopContext.loopEndOrigId
      )
    ) {
      // Visual edge: Router → LOOP_END
      addSimpleEdge(origId, loopContext.loopEndOrigId, "right", "left", false);
    }
  } else if (checkIsLoopStep(step)) {
    const loop = findPreviousLoopStep(steps, index);
    if (!loop) return;

    const loopEndId = getLoopEndOrigId(loop);

    if (!loopEndId) {
      console.error("LOOP_END not found for loop");
      return;
    }

    const loopEndIndex = steps.findIndex((s) => s.origId === loopEndId);
    if (loopEndIndex === -1) {
      console.error("LOOP_END not found in steps array");
      return;
    }

    // Move only the currently connected chain between current step and LOOP_END.
    const firstStep = steps[index + 1];
    const stepsToMove = hasVisibleEdgeBetween(
      step.origId,
      firstStep?.origId ?? ""
    )
      ? buildConnectedChainSteps(steps, index + 1).chain.filter(
          (item) => steps.indexOf(item) < loopEndIndex
        )
      : [];
    const lastStepToMove = stepsToMove[stepsToMove.length - 1];
    const shouldConnectRouterToLoopEnd = stepsToMove.length
      ? hasVisibleEdgeBetween(lastStepToMove?.origId ?? "", loopEndId)
      : hasVisibleEdgeBetween(step.origId, loopEndId);

    const movedStepOrigIds = collectAllOrigIds(stepsToMove);

    // Remove only edges that connect moved steps to external nodes
    edges.value = edges.value.filter((e) => {
      if (e.hidden) return true;

      const sourceIsMoved = movedStepOrigIds.has(e.source);
      const targetIsMoved = movedStepOrigIds.has(e.target);

      // Keep if both are inside moved steps (internal edges)
      if (sourceIsMoved && targetIsMoved) return true;

      // Keep if neither is in moved steps (unrelated edges)
      if (!sourceIsMoved && !targetIsMoved) return true;

      // Remove edges that cross the boundary (moved <-> external)
      return false;
    });

    // Remove edge from current step to first moved step
    edges.value = edges.value.filter(
      (e) => !(e.source === step.origId && e.sourceHandle?.includes("right"))
    );

    // Remove moved steps from current position
    detachChainFromList(steps, stepsToMove);

    // Remove consumed steps from loop's uiData.steps tracking
    // they will be handled in router's routes
    const removeStepFromLoopTracking = (s: any) => {
      if (!s?.origId) return;
      updateLoopStep(stepList.value, s, 0, "remove");
    };

    stepsToMove.forEach((movedStep) => {
      removeStepFromLoopTracking(movedStep);

      // Handle steps inside nested routers
      if (movedStep.type === "ROUTER" && movedStep.routes) {
        movedStep.routes.forEach((route: any) => {
          route.steps.forEach((routeStep: any) => {
            removeStepFromLoopTracking(routeStep);
          });
        });
      }
    });

    // Prepare condition steps
    const routeId = uuidv4();
    const conditionSteps =
      stepsToMove.length > 0
        ? stepsToMove.map((item) => ({
            ...item,
            parentId: routeId,
            uiData:
              item.type === "ROUTER" ||
              item.type === "LOOP" ||
              item.type === "LOOP_END" ||
              checkIsLoopStep(item)
                ? item.uiData
                : undefined,
          }))
        : [
            {
              id: `new-step-${uuidv4()}`,
              parentId: routeId,
              origId: routerStepOrigId,
              skipMissingIncomingChain: true,
              position: {
                x: routerBaseX + Math.random(),
                y: routeBaseY + Math.random(),
              },
            },
          ];
    if (stepsToMove.length > 0) {
      alignRouteStructures(conditionSteps, routerBaseX, routeBaseY);
    }

    insertBridgeBeforeLoopStart(conditionSteps, routeId);

    const newRouter: any = {
      id: parentId,
      origId,
      type: "ROUTER",
      skipMissingIncomingChain: true,
      position: {
        x: routerBaseX + Math.random(),
        y: routerBaseY + Math.random(),
      },
      ...(step.parentId && { parentId: step.parentId }),
      uiData: createRouterInsideLoopUiData(loop.origId, loopEndId),
      routes: [
        {
          id: routeId,
          parentId,
          origId: uuidv4(),
          steps: conditionSteps,
          name: "Condition 1",
          filter: null,
        },
      ],
    };
    // Assign stepNumbers: router first, then condition steps (visual order)
    assignStepNumber(newRouter, props.flow);
    conditionSteps.forEach((cs) => assignStepNumber(cs, props.flow));

    steps.splice(index + 1, 0, newRouter);
    applyScopedHorizontalAddShift({
      insertedRange: {
        startIndex: index + 1,
        endIndex: index + 1,
      },
      targetList: steps,
      anchorOrigId: step.origId,
    });
    // Register router in parent loop's step tracking
    updateLoopStep(stepList.value, newRouter, getLoopStepCount(loop), "add");

    const firstConditionStepOrigId = conditionSteps[0].origId;

    // Step -> Router
    addSimpleEdge(step.origId, origId, "right", "left", false);
    // Router → first condition step (animated)
    addSimpleEdge(origId, firstConditionStepOrigId, "bottom", "top", true);
    // Router → LOOP_END (visual) - only for top level router for each loop
    if (
      shouldConnectRouterToLoopEnd &&
      !isNestedRouterInSameLoop(step, stepList.value, loop.origId)
    ) {
      addSimpleEdge(origId, loopEndId, "right", "left", false);
    }

    // Reconnect internal chain of condition steps
    for (let i = 0; i < conditionSteps.length - 1; i++) {
      const currentOrigId = conditionSteps[i].origId;
      const nextOrigId = conditionSteps[i + 1].origId;

      if (!hasVisibleEdgeBetween(currentOrigId, nextOrigId)) {
        addSimpleEdge(currentOrigId, nextOrigId);
      }
    }

    update();

    setTimeout(() => {
      selectStepAfterAdd(origId, "router");
    }, 0);

    return;
  } else {
    // Regular case: not in a loop step, but might be in a router inside a loop
    const routeId = uuidv4();
    const nextStep = steps[index + 1];
    const edgeToNextStep = nextStep?.origId
      ? edges.value.find(
          (e) =>
            !e.hidden &&
            e.source === step.origId &&
            e.target === nextStep.origId
        )
      : undefined;
    const shouldKeepNextStepOutsideRouter =
      !!nextStep?.origId &&
      (!edgeToNextStep ||
        (!!edgeToNextStep.targetHandle &&
          edges.value.filter(
            (e) =>
              !e.hidden &&
              e.target === nextStep.origId &&
              e.targetHandle === edgeToNextStep.targetHandle
          ).length > 1));

    let parsedList = shouldKeepNextStepOutsideRouter
      ? []
      : buildConnectedChainSteps(steps, index + 1).chain;
    const lastParsedStep = parsedList[parsedList.length - 1];
    let shouldConnectRouterToLoopEnd = false;
    if (loopContext.loopEndOrigId) {
      shouldConnectRouterToLoopEnd = parsedList.length
        ? hasVisibleEdgeBetween(
            lastParsedStep?.origId ?? "",
            loopContext.loopEndOrigId
          )
        : hasVisibleEdgeBetween(step.origId, loopContext.loopEndOrigId);
    }
    if (!shouldKeepNextStepOutsideRouter) {
      detachChainFromList(steps, parsedList);
    }

    parsedList = parsedList.map((item) => ({
      ...item,
      parentId: routeId,
    }));
    if (parsedList.length > 0) {
      alignRouteStructures(parsedList, routerBaseX, routeBaseY);
    }

    const routeEntryFix = insertBridgeBeforeLoopStart(parsedList, routeId);

    const newRouter: any = {
      id: parentId,
      origId,
      type: "ROUTER",
      skipMissingIncomingChain: true,
      position: {
        x: routerBaseX + Math.random(),
        y: routerBaseY + Math.random(),
      },
      ...(step.parentId && { parentId: step.parentId }),
      routes: [
        {
          id: routeId,
          parentId,
          origId: uuidv4(),
          steps:
            parsedList.length > 0
              ? parsedList
              : [
                  {
                    id: `new-step-${uuidv4()}`,
                    parentId: routeId,
                    origId: routerStepOrigId,
                    skipMissingIncomingChain: true,
                    position: {
                      x: routerBaseX + Math.random(),
                      y: routeBaseY + Math.random(),
                    },
                  },
                ],
          name: "Condition 1",
          filter: null,
        },
      ],
    };
    // Assign stepNumbers: router first, then condition steps (visual order)
    assignStepNumber(newRouter, props.flow);
    newRouter.routes[0].steps.forEach((cs: any) =>
      assignStepNumber(cs, props.flow)
    );

    // Check if inside a loop via parent context
    if (loopContext.inLoop && loopContext.loopEndOrigId) {
      newRouter.uiData = createRouterInsideLoopUiData(
        loopContext.loopOrigId,
        loopContext.loopEndOrigId
      );

      // Only register router in parent loop's step tracking if NOT nested inside another router's condition
      // Nested routers are already contained within their parent router which is tracked in the loop
      if (!step.parentId) {
        const parentLoop = findStepByOrigId(
          stepList.value,
          loopContext.loopOrigId
        );
        if (parentLoop?.step) {
          updateLoopStep(
            stepList.value,
            newRouter,
            getLoopStepCount(parentLoop.step),
            "add"
          );
        }
      }
    }
    const insertedRouterIndex = index + 1;
    steps.splice(insertedRouterIndex, 0, newRouter);
    applyScopedHorizontalAddShift({
      insertedRange: {
        startIndex: insertedRouterIndex,
        endIndex: insertedRouterIndex,
      },
      targetList: steps,
      anchorOrigId: step.origId,
    });

    const firstCondStep = newRouter.routes[0].steps[0];
    addSimpleEdge(origId, firstCondStep.origId, "bottom", "top", true);
    if (routeEntryFix?.bridgeOrigId && routeEntryFix?.loopStartOrigId) {
      addSimpleEdge(
        routeEntryFix.bridgeOrigId,
        routeEntryFix.loopStartOrigId,
        "right",
        "left",
        false
      );
    }

    if (
      loopContext.inLoop &&
      loopContext.loopEndOrigId &&
      shouldConnectRouterToLoopEnd &&
      !isNestedInsideRouterInSameLoop(
        step,
        stepList.value,
        loopContext.loopOrigId,
        loopContext.loopEndOrigId
      )
    ) {
      addSimpleEdge(origId, loopContext.loopEndOrigId, "right", "left", false);
    }

    if (shouldKeepNextStepOutsideRouter && edgeToNextStep && nextStep?.origId) {
      addSimpleEdge(
        firstCondStep.origId,
        nextStep.origId,
        "right",
        "left",
        false
      );
    }
  }

  // Remove old edge from previous step
  edges.value = edges.value.filter((edge) => edge.source !== step.origId);
  addSimpleEdge(step.origId, origId);

  update();

  setTimeout(() => {
    selectStepAfterAdd(origId, "router");
  }, 0);
}

function openRouter(step, index, listIndex, options?: { skipSave?: boolean }) {
  emit("open-router", step, index, listIndex, options);
}

function openLoop(step, index, listIndex, options?: { skipSave?: boolean }) {
  emit("open-loop", step, index, listIndex, options);
}

function openFilter(index, step) {
  emit("show-condition", index, step);
}

function removeFilter(id) {
  const { step } = findStepById(stepList.value, id);
  step.filter = [];
  update();
}

function toggleLock(selectedStep: any) {
  const { step } = findStepById(stepList.value, selectedStep.id);
  step.lock = !step.lock;
  update();
}

function findMutableStep(stepData: any) {
  const origId = String(stepData?.origId ?? "");
  if (!origId) return null;
  return findStepByOrigId(stepList.value, origId)?.step ?? null;
}

function handleSkipStep(stepData: any) {
  const step = findMutableStep(stepData);
  if (!step) return;

  if (!skipStep(step, stepList.value)) return;

  syncAndEmitUpdate({
    initialLoad: false,
    list: stepList.value,
    edgesArr: edges.value,
    onlyLineUpdate: false,
  });
}

function handleEnableStep(stepData: any) {
  const step = findMutableStep(stepData);
  if (!step) return;

  if (!enableSkippedStep(step, stepList.value)) return;

  syncAndEmitUpdate({
    initialLoad: false,
    list: stepList.value,
    edgesArr: edges.value,
    onlyLineUpdate: false,
  });
}

function openOutput(index, step) {
  emit("open-output", index, step);
}

function openDelay(index, step) {
  emit("open-delay", index, step);
}

function cloneStep(step) {
  emit("clone-step", step);
}

function removeRouter({ step, childIndex }) {
  const { id } = step;
  if (step.routes.length === 1) {
    handleRemoveNode({ id, step });
  } else {
    const removedRouteBounds = getListBounds(
      step.routes?.[childIndex]?.steps || []
    );
    step.routes.splice(childIndex, 1);
    compactAfterRouteRemoval(step, childIndex, removedRouteBounds, step.origId);
  }
  update();
}

function setSelectedNodeId(id: string | null) {
  selectedNodeId.value = id === selectedNodeId.value ? null : id;
}

async function openConfirmModal(parameters) {
  let message: string;
  let title: string;

  // Determine the message and title based on the type of parameters
  if (typeof parameters === "string") {
    message = t("Common.DeleteFilterMessage");
    title = t("Common.DeleteFilter");
  } else if (checkIsLoop(parameters?.step)) {
    message = t("Common.DeleteLoopMessage");
    title = t("Common.DeleteLoop");
  } else if (parameters?.step?.type === "ROUTER" && parameters?.id) {
    message = t("Common.DeleteConditionMessage");
    title = t("Common.DeleteCondition");
  } else if (parameters?.id) {
    message = t("Common.DeleteStepMessage");
    title = t("Common.DeleteStep");
  } else {
    message = t("Common.DeleteBranchMessage");
    title = t("Common.DeleteBranch");
  }

  // Show the confirmation modal and wait for the user's response
  const isConfirmed = await showConfirm("warning", title, message);

  // Handle the confirmation response
  if (isConfirmed) {
    if (typeof parameters === "string") {
      removeFilter(parameters);
      emit("filter-removed", parameters);
    } else if (parameters?.id) {
      handleRemoveNode(parameters);
    } else {
      removeRouter(parameters);
    }
  }
}

function updateStepPosition(steps, updatedNode: Node) {
  // Recursively update positions in the nested stepList
  steps.forEach((step) => {
    if (step.id === updatedNode.data.id) {
      if (!step.position) {
        step.position = { x: null, y: null };
      }
      step.position.x = updatedNode.position.x;
      step.position.y = updatedNode.position.y;
      return; // Exit the current iteration
    }

    if (step.routes && step.routes.length > 0) {
      step.routes.forEach((route: any) => {
        updateStepPosition(route.steps, updatedNode);
      });
    }
  });
}

function handleChange(event) {
  if (event.node) updateStepPosition(stepList.value, event.node);
  markInvalidLoopBackConnections(edges.value);
  setTimeout(() => {
    emit("update-step-positions", stepList.value, false);
  }, 0);
}

// function use to find the right and left step of the current step based on the lines connection between them
function siblingSteps(id) {
  const foundLeftStep = edges.value.find(
    (edge) =>
      edge.target === id && edge.targetHandle?.includes("left") && !edge.hidden
  );
  const leftStep = nodes.value.find(
    (node) => node.data.origId === foundLeftStep?.source
  );
  const foundRightStep = edges.value.find(
    (edge) => edge.source === id && !edge.hidden
  );
  const rightStep = nodes.value.find(
    (node) => node.data.origId === foundRightStep?.target
  );
  return { leftStep, rightStep };
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    onContextmenu: disableContextMenu,
    onKeydown: handleKeyDown,
    onWheelCapture: handleWheelCapture,
    class: "w-full h-full flex"
  }, [
    _createVNode(_unref(VueFlow), {
      nodes: nodes.value,
      edges: visibleEdges.value,
      class: _normalizeClass([{ dark: dark.value }, "validationflow"]),
      "default-viewport": defaultViewport,
      "min-zoom": 0.2,
      "max-zoom": 2.6,
      "default-marker-color": "#492093",
      "connection-radius": 300,
      "connection-line-style": {
        strokeWidth: 4.5,
        stroke: '#68affc',
        strokeDasharray: '6 6',
      },
      onNodeDragStop: handleChange,
      "nodes-draggable": _ctx.nodesDraggable,
      onConnect: handleConnect,
      "delete-key-code": null
    }, {
      "edge-custom": _withCtx((customEdgeProps) => [
        _createVNode(CustomEdge, {
          nodes: nodes.value,
          edges: edges.value,
          "step-list": stepList.value,
          "nodes-draggable": _ctx.nodesDraggable,
          "show-input-summary": _ctx.showInputSummary,
          "edge-type": "node",
          "edge-class": "customNodeEdge",
          isLoopEdge: 
            String(customEdgeProps.sourceHandleId || '').includes(
              '__handle-top'
            ) &&
            String(customEdgeProps.targetHandleId || '').includes(
              '__handle-top'
            )
          ,
          "selected-edge": selectedEdge.value,
          id: customEdgeProps.id,
          data: customEdgeProps.sourceNode.data,
          "target-step-data": customEdgeProps.targetNode?.data,
          isAnimated: customEdgeProps.animated,
          router: edgeRouterLookup.value.get(customEdgeProps?.target) ?? '',
          "marker-end": customEdgeProps.markerEnd,
          parrentIndex: customEdgeProps.label,
          "source-x": customEdgeProps.sourceX,
          "source-y": customEdgeProps.sourceY,
          "target-x": customEdgeProps.targetX,
          "target-y": customEdgeProps.targetY,
          "source-position": customEdgeProps.sourcePosition,
          "target-position": customEdgeProps.targetPosition,
          onOpenFilter: openFilter,
          onRemoveFilter: openConfirmModal,
          onRemoveRouter: openConfirmModal
        }, null, 8, ["nodes", "edges", "step-list", "nodes-draggable", "show-input-summary", "isLoopEdge", "selected-edge", "id", "data", "target-step-data", "isAnimated", "router", "marker-end", "parrentIndex", "source-x", "source-y", "target-x", "target-y", "source-position", "target-position"])
      ]),
      "node-start": _withCtx((customNodeProps) => [
        _createVNode(CustomNode, {
          "removal-graph": removalGraph.value,
          "show-input-summary": _ctx.showInputSummary,
          onAddNodeAfterStart: handleAddnodeAfterStart,
          "selected-step": _ctx.selectedStep,
          type: "node",
          nodes: nodes.value,
          edges: edges.value,
          flowLog: _ctx.flowLog,
          "node-info": asNodeProps(customNodeProps),
          "nodes-draggable": _ctx.nodesDraggable
        }, null, 8, ["removal-graph", "show-input-summary", "selected-step", "nodes", "edges", "flowLog", "node-info", "nodes-draggable"])
      ]),
      "node-custom": _withCtx((customNodeProps) => [
        _createVNode(CustomNode, {
          "router-branch-lookup": routerBranchLookup.value,
          "active-loop-reference-orig-ids": activeLoopReferenceOrigIds.value,
          "removal-graph": removalGraph.value,
          "show-input-summary": _ctx.showInputSummary,
          type: "node",
          nodes: nodes.value,
          edges: edges.value,
          "list-index": '',
          logs: _ctx.logs?.find((x) => x?.stepId === customNodeProps.data.id),
          flowLog: _ctx.flowLog,
          "node-info": asNodeProps(customNodeProps),
          isNextStepLoop: loopNeighborLookup.value.get(customNodeProps.id) ?? false,
          isNextStepLoopEnd: 
            nextLoopEndLookup.value.get(asNodeProps(customNodeProps).data.origId) ??
            false
          ,
          "selected-Step": _ctx.selectedStep,
          selectedNodeId: selectedNodeId.value,
          clonedStep: _ctx.clonedStep,
          onOpenLoopSettings: openLoop,
          onOpenFilter: openFilter,
          onAddOrRemoveExpressionType: addOrRemoveExpressionType,
          onSetSelectedNodeId: setSelectedNodeId,
          onOpenDrawer: openDrawer,
          onRemoveNode: openConfirmModal,
          onAddNode: handleAddNode,
          onAddRouter: addRouter,
          onDelay: openDelay,
          onCloneStep: cloneStep,
          onPasteStep: pasteStep,
          onOpenOutput: openOutput,
          onToggleLock: toggleLock,
          onSkipStep: handleSkipStep,
          onEnableStep: handleEnableStep,
          "step-list": stepList.value,
          "nodes-draggable": _ctx.nodesDraggable
        }, null, 8, ["router-branch-lookup", "active-loop-reference-orig-ids", "removal-graph", "show-input-summary", "nodes", "edges", "logs", "flowLog", "node-info", "isNextStepLoop", "isNextStepLoopEnd", "selected-Step", "selectedNodeId", "clonedStep", "step-list", "nodes-draggable"])
      ]),
      "node-router": _withCtx((customRouteProps) => [
        _createVNode(CustomNode, {
          "active-loop-reference-orig-ids": activeLoopReferenceOrigIds.value,
          "removal-graph": removalGraph.value,
          "show-input-summary": _ctx.showInputSummary,
          type: "router",
          nodes: nodes.value,
          edges: edges.value,
          "list-index": _ctx.listIndex,
          "selected-step": _ctx.selectedStep,
          selectedNodeId: selectedNodeId.value,
          flowLog: _ctx.flowLog,
          "node-info": asNodeProps(customRouteProps),
          onOpenRouterSettings: openRouter,
          onSetSelectedNodeId: setSelectedNodeId,
          onAddNode: handleAddNode,
          onAddRouter: addRouter,
          onRemoveNode: openConfirmModal,
          onSkipStep: handleSkipStep,
          onEnableStep: handleEnableStep,
          "step-list": stepList.value,
          "nodes-draggable": _ctx.nodesDraggable
        }, null, 8, ["active-loop-reference-orig-ids", "removal-graph", "show-input-summary", "nodes", "edges", "list-index", "selected-step", "selectedNodeId", "flowLog", "node-info", "step-list", "nodes-draggable"])
      ]),
      "node-loopEnd": _withCtx((customNodeProps) => [
        _createVNode(CustomNode, {
          "active-loop-reference-orig-ids": activeLoopReferenceOrigIds.value,
          "removal-graph": removalGraph.value,
          "show-input-summary": _ctx.showInputSummary,
          type: "node",
          nodes: nodes.value,
          edges: edges.value,
          "list-index": '',
          logs: _ctx.logs?.find((x) => x?.stepId === customNodeProps.data.id),
          flowLog: _ctx.flowLog,
          "node-info": asNodeProps(customNodeProps),
          "selected-Step": _ctx.selectedStep,
          selectedNodeId: selectedNodeId.value,
          clonedStep: _ctx.clonedStep,
          onSetSelectedNodeId: setSelectedNodeId,
          onOpenDrawer: openDrawer,
          onRemoveNode: openConfirmModal,
          onAddNode: handleAddNode,
          onSkipStep: handleSkipStep,
          onEnableStep: handleEnableStep,
          "step-list": stepList.value,
          "nodes-draggable": _ctx.nodesDraggable
        }, null, 8, ["active-loop-reference-orig-ids", "removal-graph", "show-input-summary", "nodes", "edges", "logs", "flowLog", "node-info", "selected-Step", "selectedNodeId", "clonedStep", "step-list", "nodes-draggable"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (_ctx.showOrganizeSteps)
            ? (_openBlock(), _createBlock(_unref(ElTooltip), {
                key: 0,
                effect: "custom",
                content: _unref(t)('Common.OrganizeSteps'),
                placement: "top"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("button", {
                    onClick: handleOrganizeClick,
                    class: "icon-button"
                  }, [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "sitemap",
                      class: "icon-color"
                    })
                  ])
                ]),
                _: 1
              }, 8, ["content"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(ElTooltip), {
            effect: "custom",
            content: _unref(t)('Common.CenterFlow'),
            placement: "top"
          }, {
            default: _withCtx(() => [
              _createElementVNode("button", {
                onClick: locateFlow,
                class: "icon-button"
              }, [
                _createVNode(_component_font_awesome_icon, {
                  icon: "expand",
                  class: "icon-color"
                })
              ])
            ]),
            _: 1
          }, 8, ["content"]),
          _createVNode(_unref(ElSlider), {
            modelValue: zoomScale.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((zoomScale).value = $event)),
            step: 10,
            "show-stops": "",
            min: 20,
            max: 200,
            "format-tooltip": (t) => t + '%',
            onChange: zoom,
            class: "custom-slider",
            "tooltip-class": "custom-slider"
          }, null, 8, ["modelValue", "format-tooltip"])
        ]),
        _createVNode(_unref(Background), {
          "pattern-color": "#aaa",
          "bg-color": _ctx.backgroundColor,
          gap: 16
        }, null, 8, ["bg-color"])
      ]),
      _: 1
    }, 8, ["nodes", "edges", "class", "nodes-draggable"])
  ], 32))
}
}

})